import React, { useState, useMemo, useEffect, useContext, useRef, useCallback } from "react";
import {
  Paper,
  Grid,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Skeleton,
  MenuItem,
  Select,
  FormControl,
  Container,
  InputLabel,
  InputAdornment,
  Card,
  Slide,
  Divider,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  alpha,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowForward as ArrowForwardIcon,
  Download as DownloadIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  LocalAtm as LocalAtmIcon,
  AccountBalance as AccountBalanceIcon,
  Diamond as DiamondIcon,
  Paid as PaidIcon,
  Info as InfoIcon,
  Summarize as SummarizeIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  CalendarToday,
  ArrowUpward,
  ArrowDownward,
  AccountBalanceWallet,
} from "@mui/icons-material";
import { DataContext } from "../../context/DataContext";
import {
  format,
  parseISO,
  // isWithinInterval,
  // startOfDay,
  // endOfDay,
  addDays,
} from "date-fns";
import { useTheme } from "@mui/material/styles";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Skeleton Components
const SummaryCardSkeleton = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: "20px",
        backgroundColor: 'transparent',
        backdropFilter: "blur(10px)",
        border: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Skeleton 
          variant="circular" 
          width={40} 
          height={40} 
          sx={{ mr: 2,  }} 
        />
        <Skeleton 
          variant="text" 
          width={150} 
          height={32}  
        />
      </Box>
      <Stack spacing={2}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Skeleton variant="text" width={100} height={24} />
          <Skeleton variant="text" width={80} height={24} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Skeleton variant="text" width={100} height={24} />
          <Skeleton variant="text" width={80} height={24} />
        </Box>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Skeleton variant="text" width={100} height={28}  />
          <Skeleton variant="text" width={100} height={36} />
        </Box>
      </Stack>
    </Paper>
  );
};

const DateSelectorSkeleton = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: "20px",
        backgroundColor: 'transparent',
        backdropFilter: "blur(10px)",
        border: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
        height: "100%",
      }}
    >
      <Skeleton variant="text" width={150} height={32} sx={{ mb: 2,}} />
      <Skeleton variant="rounded" width="100%" height={56} sx={{ mb: 2, borderRadius: "12px", }} />
      <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: "12px", }} />
    </Paper>
  );
};

const OpeningBalanceSkeleton = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: "20px",
        backgroundColor: 'transparent',
        backdropFilter: "blur(10px)",
        border: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
        height: "100%",
      }}
    >
      <Skeleton variant="text" width={150} height={32} sx={{ mb: 2, }} />
      <Stack spacing={2}>
        <Skeleton variant="rounded" width="100%" height={56} sx={{ borderRadius: "12px",  }} />
        <Skeleton variant="rounded" width="100%" height={56} sx={{ borderRadius: "12px", }}/>
      </Stack>
    </Paper>
  );
};

const TransactionStatsSkeleton = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: "20px",
        backgroundColor: 'transparent',
        backdropFilter: "blur(10px)",
        border: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
        height: "100%",
      }}
    >
      <Skeleton variant="text" width={150} height={32} sx={{ mb: 2,}} />
      <Stack spacing={1.5}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Skeleton variant="text" width={120} height={24}/>
          <Skeleton variant="rounded" width={40} height={24} sx={{ borderRadius: "16px", }} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Skeleton variant="text" width={120} height={24}/>
          <Skeleton variant="rounded" width={40} height={24} sx={{ borderRadius: "16px",}} />
        </Box>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Skeleton variant="text" width={120} height={28} />
          <Skeleton variant="rounded" width={60} height={32} sx={{ borderRadius: "16px", }} />
        </Box>
      </Stack>
    </Paper>
  );
};

const DayActionsSkeleton = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  return (
    <Paper
      sx={{
        width: "330px",
        p: 2,
        borderRadius: "20px",
        backgroundColor: 'transparent',
        backdropFilter: "blur(10px)",
        border: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
        height: "100%",
      }}
    >
      <Skeleton variant="text" width={120} height={32} sx={{ mb: 2,}} />
      <Stack spacing={2}>
        <Skeleton variant="rounded" width="100%" height={48} sx={{ borderRadius: "12px",  }} />
        <Skeleton variant="rounded" width="100%" height={56} sx={{ borderRadius: "12px",}} />
      </Stack>
    </Paper>
  );
};

const QuickAddSkeleton = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  return (
    <Paper
      sx={{
        p: 2,
        mb: 3,
        borderRadius: "20px",
        backgroundColor: 'transparent',
        // background: isDark
        //   ? "rgba(30, 41, 59, 0.5)"
        //   : "rgba(255, 255, 255, 0.8)",
        backdropFilter: "blur(10px)",
        border: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
      }}
    >
      <Skeleton variant="text" width={200} height={24} sx={{ mb: 1.5,}} />
      <Stack direction="row" spacing={1} flexWrap="wrap" gap={2} sx={{ alignContent: "center", justifyContent: "center" }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="rounded" width={130} height={36} sx={{ borderRadius: "12px", }} />
        ))}
      </Stack>
    </Paper>
  );
};

const BalancePositionSkeleton = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  return (
    <Paper
      sx={{
        width: "600px",
        p: 2,
        borderRadius: "20px",
        backgroundColor: 'transparent',
        backdropFilter: "blur(10px)",
        border: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
      }}
    >
      <Skeleton variant="text" width={200} height={32} sx={{ mx: "auto", mb: 3, }} />
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={2.4} key={i}>
            <Paper
              sx={{
                p: 1.5,
                textAlign: "center",
                borderRadius: "16px",
                backgroundColor: 'transparent',
                border: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
              }}
            >
              <Skeleton variant="circular" width={43} height={43} sx={{ mx: "auto", mb: 1,  }} />
              <Skeleton variant="text" width={80} height={20} sx={{ mx: "auto", mb: 0.5,  }} />
              <Skeleton variant="text" width={100} height={28} sx={{ mx: "auto", mb: 0.5,  }} />
              <Skeleton variant="text" width={60} height={16} sx={{ mx: "auto",}} />
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

const TableSkeleton = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  return (
    <Paper
      sx={{
        borderRadius: "20px",
        backgroundColor: 'transparent',
        backdropFilter: "blur(20px)",
        border: `1px solid ${alpha(
          isDark ? "#475569" : "#e2e8f0",
          isDark ? 0.2 : 0.3
        )}`,
        mb: 3,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${alpha(
            isDark ? "#475569" : "#e2e8f0",
            isDark ? 0.2 : 0.3
          )}`,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Skeleton variant="text" width={200} height={32} />
          <Skeleton variant="text" width={150} height={32} />
        </Box>
      </Box>
      <Box sx={{ p: 3 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Box key={i} sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Skeleton variant="rounded" width="100%" height={60} sx={{ borderRadius: "8px", }} />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const JewelleryDaybook = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const {
    // customerTransactions,
    // supplierTransactions,
    // customers,
    // suppliers,
    loading,
    roznama,
    closeRoznamaDay,
    refresh,
    updateRoznama,
    addRoznama,
    findRoznamaByDate
  } = useContext(DataContext);

  console.log("Roznama from context:", roznama);
  const [selectedDate, setSelectedDate] = useState(
    format(new Date(), "yyyy-MM-dd")
  );
  const [selectedRoznama, setSelectedRoznama] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [loadingRoznama, setLoadingRoznama] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  // const [pageLoaded, setPageLoaded] = useState(false);
  const [dailyReportData, setDailyReportData] = useState(null);
  const [showDailyReport, setShowDailyReport] = useState(false);

  const [viewTransactionDialog, setViewTransactionDialog] = useState(false);
  const [viewingTransaction, setViewingTransaction] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);


  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  // Add this near your other state declarations
  // const [adjustmentMode, setAdjustmentMode] = useState(false);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [openingBalance, setOpeningBalance] = useState({
    cash: 0,
    bank: 0,
  });
  const [manualTransactions, setManualTransactions] = useState({
    income: [],
    expense: [],
  });
  const [isClosingDay, setIsClosingDay] = useState(false);
  const [newTransaction, setNewTransaction] = useState({
    type: "income",
    billNumber: "",
    category: "",
    description: "",
    amount: "",
    paymentMode: "cash",
    remark: "",
    cash: "",
    bank: "",
    cashPortion: "",
    bankPortion: "",
    fine: "",
    finePaid: "",
  });
  const loadingRef = useRef(false);
  // Table states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("date");

  // Transaction categories
  const incomeCategories = [
    "Sale",
    "Interest Income",
    "Commission",
    "Service Charge",
    "Rent Income",
    "Miscellaneous Income",
    "Loan Recovery",
    "Investment Return",
  ];

  const expenseCategories = [
    "Purchase",
    "Salary",
    "Rent",
    "Utilities",
    "Maintenance",
    "Transportation",
    "Marketing",
    "Office Supplies",
    "Professional Fees",
    "Taxes",
    "Miscellaneous Expense",
    "Loan Payment",
  ];

  // Add these state variables near your other state declarations
  const [closeDayDialogOpen, setCloseDayDialogOpen] = useState(false);
  const [actualBalances, setActualBalances] = useState({
    cash: 0,
    bank: 0
  });
  const [differences, setDifferences] = useState({
    cash: 0,
    bank: 0,
    total: 0
  });
  // const [suspenseEntry, setSuspenseEntry] = useState(null);

  // Function to handle viewing transaction details
  const handleViewTransactionDetails = (transaction) => {
    setViewingTransaction(transaction);
    setViewTransactionDialog(true);
  };

  // Function to close the view dialog
  const handleCloseViewDialog = () => {
    setViewTransactionDialog(false);
    setViewingTransaction(null);
  };

  // Function to handle the close day button click - REPLACE the existing handleCloseDay function
  const handleCloseDayClick = () => {
    // First calculate the differences
    const cashDifference = summary.closingBalanceCash;
    const bankDifference = summary.closingBalanceBank;
    const totalDifference = summary.totalClosingBalance;

    setDifferences({
      cash: cashDifference,
      bank: bankDifference,
      total: totalDifference
    });

    // Set initial actual balances to calculated balances
    setActualBalances({
      cash: summary.closingBalanceCash,
      bank: summary.closingBalanceBank
    });

    // Open the close day dialog
    setCloseDayDialogOpen(true);
  };

  // Function to handle actual balance changes
  const handleActualBalanceChange = (field, value) => {
    const newValue = parseFloat(value) || 0;
    console.log("New actual balance for", field, ":", newValue);
    setActualBalances(prev => ({
      ...prev,
      [field]: newValue
    }));

    // Recalculate differences
    const cashDifference = field === 'cash' ? newValue - summary.closingBalanceCash : actualBalances.cash - summary.closingBalanceCash;
    const bankDifference = field === 'bank' ? newValue - summary.closingBalanceBank : actualBalances.bank - summary.closingBalanceBank;
    console.log("Recalculated differences - Cash:", cashDifference, "Bank:", bankDifference);
    setDifferences({
      cash: cashDifference,
      bank: bankDifference,
      total: cashDifference + bankDifference
    });
  };

  // Function to create suspense entry and close the day
  const handleConfirmCloseDay = async () => {
    setIsClosingDay(true);

    try {
      // Check if there are differences
      const hasDifference = differences.cash !== 0 || differences.bank !== 0;

      // Create suspense entry if there's a difference
      let suspenseTransaction = null;
      if (hasDifference) {
        suspenseTransaction = {
          id: `suspense-${Date.now()}`,
          date: selectedDate,
          type: differences.total > 0 ? "income" : "expense",
          billNumber: `SUSP-${format(new Date(), 'yyMMddHHmmss')}`,
          category: differences.total > 0 ? "Suspense Income" : "Suspense Expense",
          description: `Suspense adjustment for ${format(parseISO(selectedDate), 'dd/MM/yyyy')}`,
          amount: Math.abs(differences.total),
          paymentMode: "cash", // This is a virtual transaction
          remark: `Cash diff: ₹${differences.cash.toLocaleString('en-IN')}, Bank diff: ₹${differences.bank.toLocaleString('en-IN')}`,
          cash: Math.abs(differences.cash),
          bank: Math.abs(differences.bank),
          transactionType: "suspense",
          isAutoGenerated: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          totalReceived: differences.total > 0 ? Math.abs(differences.total) : 0,
          totalPaid: differences.total < 0 ? Math.abs(differences.total) : 0,
          totalPending: 0
        };

        // Add suspense transaction to manual transactions
        if (differences.total > 0) {
          setManualTransactions(prev => ({
            ...prev,
            income: [...prev.income, suspenseTransaction]
          }));
        } else {
          setManualTransactions(prev => ({
            ...prev,
            expense: [...prev.expense, suspenseTransaction]
          }));
        }
      }

      // Combine all transactions including suspense
      const allIncomeTransactions = [
        ...manualTransactions.income,
        ...autoIncomeTransactions,
        ...(differences.total > 0 ? [suspenseTransaction] : [])
      ];
      const allExpenseTransactions = [
        ...manualTransactions.expense,
        ...autoExpenseTransactions,
        ...(differences.total < 0 ? [suspenseTransaction] : [])
      ];

      // Calculate final closing balances (should match actual balances)
      const finalClosingBalanceCash = actualBalances.cash;
      const finalClosingBalanceBank = actualBalances.bank;
      // const totalClosingBalance = finalClosingBalanceCash + finalClosingBalanceBank;

      // Calculate totals including suspense
      let cashIncome = 0;
      let bankIncome = 0;
      let cashExpense = 0;
      let bankExpense = 0;

      allIncomeTransactions.forEach((t) => {
        cashIncome += parseFloat(t.cash) || 0;
        bankIncome += parseFloat(t.bank) || 0;
      });

      allExpenseTransactions.forEach((t) => {
        cashExpense += parseFloat(t.cash) || 0;
        bankExpense += parseFloat(t.bank) || 0;
      });

      const totalIncome = cashIncome + bankIncome;
      const totalExpense = cashExpense + bankExpense;
      const netProfit = totalIncome - totalExpense;

      const response = await closeRoznamaDay(
        selectedDate,
        openingBalance.cash,
        openingBalance.bank,
        allIncomeTransactions,
        allExpenseTransactions,
        finalClosingBalanceBank,  // Use actual bank balance
        finalClosingBalanceCash,  // Use actual cash balance
        netProfit,
        totalIncome,
        totalExpense
      );

      if (response.success) {
        showSnackbar("Day closed successfully with suspense entry!", "success");
        await refresh();
        const nextDay = format(addDays(parseISO(selectedDate), 1), "yyyy-MM-dd");
        setSelectedDate(nextDay);
        setManualTransactions({
          income: [],
          expense: [],
        });
        // setSuspenseEntry(suspenseTransaction);
        setCloseDayDialogOpen(false);
      } else {
        showSnackbar(`Error: ${response.error || "Failed to close day"}`, "error");
      }
    } catch (error) {
      showSnackbar(`Error: ${error.message}`, "error");
    } finally {
      setIsClosingDay(false);
    }
  };

  // Function to open adjustment dialog for past dates
  const handleOpenAdjustmentDialog = () => {
    // Check if it's a past day
    if (!isPastDay() && !isDayClosed(selectedRoznama)) {
      showSnackbar("Adjustments are only allowed for past/closed days", "warning");
      return;
    }

    // Reset the new transaction for adjustment
    const billPrefix = "ADJ";
    setNewTransaction({
      type: "income", // default to income, user can change
      billNumber: `${billPrefix}-${format(new Date(), 'yyMMddHHmmss')}`,
      category: "Adjustment",
      description: "",
      amount: "",
      paymentMode: "cash",
      remark: `Adjustment for ${format(parseISO(selectedDate), 'dd/MM/yyyy')}`,
      cash: "",
      bank: "",
      cashPortion: "",
      bankPortion: "",
      fine: "",
      finePaid: "",
      isAdjustment: true, // Flag to identify adjustment transactions
    });
    setEditingTransaction(null);
    // setAdjustmentMode(true);
    setAdjustmentDialogOpen(true);
  };

  // Function to handle saving adjustment transactions
  // Function to handle saving adjustment transactions (MODIFIED)
  const handleSubmitAdjustment = async () => {
    if (!newTransaction.amount || !newTransaction.description) {
      showSnackbar("Please enter amount and description", "warning");
      return;
    }

    // Calculate cash and bank portions
    let cashAmount = 0;
    let bankAmount = 0;

    if (newTransaction.paymentMode === "split") {
      cashAmount = parseFloat(newTransaction.cashPortion) || 0;
      bankAmount = parseFloat(newTransaction.bankPortion) || 0;
    } else if (newTransaction.paymentMode === "cash") {
      cashAmount = parseFloat(newTransaction.amount) || 0;
      bankAmount = 0;
    } else if (newTransaction.paymentMode === "bank") {
      cashAmount = 0;
      bankAmount = parseFloat(newTransaction.amount) || 0;
    }

    // Find existing suspense entry
    const existingSuspenseEntry = [
      ...manualTransactions.income,
      ...manualTransactions.expense
    ].find(t => t.transactionType === "suspense");

    try {
      let updatedManualTransactions = { ...manualTransactions };

      if (existingSuspenseEntry) {
        // Update existing suspense entry
        const updatedSuspenseEntry = { ...existingSuspenseEntry };

        // Calculate adjustments
        if (newTransaction.type === "income") {
          // Adding income means we previously had less income than recorded
          // So we need to reduce suspense income or increase suspense expense
          if (updatedSuspenseEntry.type === "income") {
            // Reduce suspense income (meaning we had MORE actual money than recorded)
            updatedSuspenseEntry.amount -= parseFloat(newTransaction.amount) || 0;
            updatedSuspenseEntry.cash -= cashAmount;
            updatedSuspenseEntry.bank -= bankAmount;

            // If amount becomes negative, flip to expense
            if (updatedSuspenseEntry.amount < 0) {
              updatedSuspenseEntry.type = "expense";
              updatedSuspenseEntry.amount = Math.abs(updatedSuspenseEntry.amount);
              updatedSuspenseEntry.cash = Math.abs(updatedSuspenseEntry.cash);
              updatedSuspenseEntry.bank = Math.abs(updatedSuspenseEntry.bank);
              updatedSuspenseEntry.category = "Suspense Expense";
              updatedSuspenseEntry.totalPaid = updatedSuspenseEntry.amount;
              updatedSuspenseEntry.totalReceived = 0;
            } else {
              updatedSuspenseEntry.totalReceived = updatedSuspenseEntry.amount;
              updatedSuspenseEntry.totalPaid = 0;
            }
          } else {
            // Increase suspense expense (meaning we had LESS actual money than recorded)
            updatedSuspenseEntry.amount += parseFloat(newTransaction.amount) || 0;
            updatedSuspenseEntry.cash += cashAmount;
            updatedSuspenseEntry.bank += bankAmount;
            updatedSuspenseEntry.totalPaid = updatedSuspenseEntry.amount;
          }
        } else {
          // Adding expense means we previously had less expense than recorded
          // So we need to increase suspense income or reduce suspense expense
          if (updatedSuspenseEntry.type === "expense") {
            // Reduce suspense expense (meaning we had MORE actual money than recorded)
            updatedSuspenseEntry.amount -= parseFloat(newTransaction.amount) || 0;
            updatedSuspenseEntry.cash -= cashAmount;
            updatedSuspenseEntry.bank -= bankAmount;

            // If amount becomes negative, flip to income
            if (updatedSuspenseEntry.amount < 0) {
              updatedSuspenseEntry.type = "income";
              updatedSuspenseEntry.amount = Math.abs(updatedSuspenseEntry.amount);
              updatedSuspenseEntry.cash = Math.abs(updatedSuspenseEntry.cash);
              updatedSuspenseEntry.bank = Math.abs(updatedSuspenseEntry.bank);
              updatedSuspenseEntry.category = "Suspense Income";
              updatedSuspenseEntry.totalReceived = updatedSuspenseEntry.amount;
              updatedSuspenseEntry.totalPaid = 0;
            } else {
              updatedSuspenseEntry.totalPaid = updatedSuspenseEntry.amount;
              updatedSuspenseEntry.totalReceived = 0;
            }
          } else {
            // Increase suspense income (meaning we had LESS actual money than recorded)
            updatedSuspenseEntry.amount += parseFloat(newTransaction.amount) || 0;
            updatedSuspenseEntry.cash += cashAmount;
            updatedSuspenseEntry.bank += bankAmount;
            updatedSuspenseEntry.totalReceived = updatedSuspenseEntry.amount;
          }
        }

        // Update remark to include adjustment details
        const adjustmentRemark = `${newTransaction.type === "income" ? "Income" : "Expense"} adjustment: ${newTransaction.description} (₹${parseFloat(newTransaction.amount).toLocaleString('en-IN')})`;
        updatedSuspenseEntry.remark = updatedSuspenseEntry.remark
          ? `${updatedSuspenseEntry.remark}; ${adjustmentRemark}`
          : adjustmentRemark;
        updatedSuspenseEntry.updated_at = new Date().toISOString();

        // Update in manual transactions
        if (updatedSuspenseEntry.type === "income") {
          updatedManualTransactions = {
            ...updatedManualTransactions,
            income: updatedManualTransactions.income.map(t =>
              t.id === existingSuspenseEntry.id ? updatedSuspenseEntry : t
            ),
          };

          // Remove from expense if it flipped from expense to income
          if (existingSuspenseEntry.type === "expense") {
            updatedManualTransactions = {
              ...updatedManualTransactions,
              expense: updatedManualTransactions.expense.filter(t =>
                t.id !== existingSuspenseEntry.id
              ),
            };
          }
        } else {
          updatedManualTransactions = {
            ...updatedManualTransactions,
            expense: updatedManualTransactions.expense.map(t =>
              t.id === existingSuspenseEntry.id ? updatedSuspenseEntry : t
            ),
          };

          // Remove from income if it flipped from income to expense
          if (existingSuspenseEntry.type === "income") {
            updatedManualTransactions = {
              ...updatedManualTransactions,
              income: updatedManualTransactions.income.filter(t =>
                t.id !== existingSuspenseEntry.id
              ),
            };
          }
        }

        // Also add the adjustment as a separate record (for tracking)
        const adjustmentRecord = {
          id: `adj-record-${Date.now()}`,
          date: selectedDate,
          type: newTransaction.type,
          billNumber: `ADJ-${format(new Date(), 'yyMMddHHmmss')}`,
          category: "Adjustment",
          description: newTransaction.description,
          amount: parseFloat(newTransaction.amount) || 0,
          paymentMode: newTransaction.paymentMode,
          remark: `Adjustment applied to suspense entry`,
          cash: cashAmount,
          bank: bankAmount,
          transactionType: "adjustment",
          isAdjustment: true,
          isAdjustmentRecord: true, // Flag to identify as adjustment record
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          totalReceived: newTransaction.type === "income" ? parseFloat(newTransaction.amount) || 0 : 0,
          totalPaid: newTransaction.type === "expense" ? parseFloat(newTransaction.amount) || 0 : 0,
          totalPending: 0,
        };

        if (newTransaction.type === "income") {
          updatedManualTransactions = {
            ...updatedManualTransactions,
            income: [...updatedManualTransactions.income, adjustmentRecord],
          };
        } else {
          updatedManualTransactions = {
            ...updatedManualTransactions,
            expense: [...updatedManualTransactions.expense, adjustmentRecord],
          };
        }

      } else {
        // No existing suspense entry - create a new one
        const suspenseTransaction = {
          id: `suspense-${Date.now()}`,
          date: selectedDate,
          type: newTransaction.type === "income" ? "expense" : "income", // Opposite of adjustment
          billNumber: `SUSP-ADJ-${format(new Date(), 'yyMMddHHmmss')}`,
          category: newTransaction.type === "income" ? "Suspense Expense" : "Suspense Income",
          description: `Suspense adjustment created for: ${newTransaction.description}`,
          amount: parseFloat(newTransaction.amount) || 0,
          paymentMode: newTransaction.paymentMode,
          remark: `Created for: ${newTransaction.description} (₹${parseFloat(newTransaction.amount).toLocaleString('en-IN')})`,
          cash: cashAmount,
          bank: bankAmount,
          transactionType: "suspense",
          isAutoGenerated: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          totalReceived: newTransaction.type === "income" ? 0 : parseFloat(newTransaction.amount) || 0,
          totalPaid: newTransaction.type === "income" ? parseFloat(newTransaction.amount) || 0 : 0,
          totalPending: 0,
        };

        // Add suspense entry
        if (suspenseTransaction.type === "income") {
          updatedManualTransactions = {
            ...updatedManualTransactions,
            income: [...updatedManualTransactions.income, suspenseTransaction],
          };
        } else {
          updatedManualTransactions = {
            ...updatedManualTransactions,
            expense: [...updatedManualTransactions.expense, suspenseTransaction],
          };
        }

        // Add adjustment record
        const adjustmentRecord = {
          id: `adj-record-${Date.now()}`,
          date: selectedDate,
          type: newTransaction.type,
          billNumber: `ADJ-${format(new Date(), 'yyMMddHHmmss')}`,
          category: "Adjustment",
          description: newTransaction.description,
          amount: parseFloat(newTransaction.amount) || 0,
          paymentMode: newTransaction.paymentMode,
          remark: `Created suspense entry for this adjustment`,
          cash: cashAmount,
          bank: bankAmount,
          transactionType: "adjustment",
          isAdjustment: true,
          isAdjustmentRecord: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          totalReceived: newTransaction.type === "income" ? parseFloat(newTransaction.amount) || 0 : 0,
          totalPaid: newTransaction.type === "expense" ? parseFloat(newTransaction.amount) || 0 : 0,
          totalPending: 0,
        };

        if (newTransaction.type === "income") {
          updatedManualTransactions = {
            ...updatedManualTransactions,
            income: [...updatedManualTransactions.income, adjustmentRecord],
          };
        } else {
          updatedManualTransactions = {
            ...updatedManualTransactions,
            expense: [...updatedManualTransactions.expense, adjustmentRecord],
          };
        }
      }

      // Update local state
      setManualTransactions(updatedManualTransactions);

      // Calculate totals with updated transactions
      const allIncomeTransactions = [
        ...updatedManualTransactions.income,
        ...autoIncomeTransactions,
      ];
      const allExpenseTransactions = [
        ...updatedManualTransactions.expense,
        ...autoExpenseTransactions,
      ];

      // Calculate totals (but keep original closing balances)
      let cashIncome = 0;
      let bankIncome = 0;
      let cashExpense = 0;
      let bankExpense = 0;

      allIncomeTransactions.forEach((t) => {
        cashIncome += parseFloat(t.cash) || 0;
        bankIncome += parseFloat(t.bank) || 0;
      });

      allExpenseTransactions.forEach((t) => {
        cashExpense += parseFloat(t.cash) || 0;
        bankExpense += parseFloat(t.bank) || 0;
      });

      const totalIncome = cashIncome + bankIncome;
      const totalExpense = cashExpense + bankExpense;
      const netProfit = totalIncome - totalExpense;

      // IMPORTANT: Keep the ORIGINAL closing balances from selectedRoznama
      // This ensures physical cash/bank balances don't change
      const closingBalanceCash = selectedRoznama?.closingBalanceCash || summary.closingBalanceCash;
      const closingBalanceBank = selectedRoznama?.closingBalanceBank || summary.closingBalanceBank;

      // Update the roznama in backend (keeping original closing balances)
      const roznamaUpdateData = {
        date: selectedDate,
        openingBalanceCash: openingBalance.cash,
        openingBalanceBank: openingBalance.bank,
        closingBalanceCash, // Keep original
        closingBalanceBank, // Keep original
        incomeTransactions: allIncomeTransactions,
        expenseTransactions: allExpenseTransactions,
        netProfit,
        totalIncome,
        totalExpense,
        lastAdjusted: new Date().toISOString(),
        hasAdjustments: true,
      };

      if (selectedRoznama?.id) {
        const response = await updateRoznama(selectedRoznama.id, roznamaUpdateData);

        if (response.success) {
          showSnackbar("Adjustment saved! Suspense entry updated.", "success");
          await refresh();

          // Update local state
          setSelectedRoznama(prev => ({
            ...prev,
            ...roznamaUpdateData,
          }));

          setAdjustmentDialogOpen(false);
          // setAdjustmentMode(false);
        } else {
          showSnackbar(`Failed to save adjustment: ${response.error}`, "error");
          // Revert local state
          setManualTransactions(manualTransactions);
        }
      } else {
        showSnackbar("Cannot add adjustment - no roznama found", "error");
      }
    } catch (error) {
      console.error("Error saving adjustment:", error);
      showSnackbar("Error saving adjustment", "error");
      setManualTransactions(manualTransactions);
    }
  };

  // Function to edit suspense entry directly
  const handleEditSuspenseEntry = (suspenseEntry) => {
    setEditingTransaction(suspenseEntry);

    let paymentMode = "cash";
    if (suspenseEntry.bank > 0 && suspenseEntry.cash > 0) {
      paymentMode = "split";
    } else if (suspenseEntry.bank > 0) {
      paymentMode = "bank";
    }

    setNewTransaction({
      type: suspenseEntry.type,
      billNumber: suspenseEntry.billNumber,
      category: suspenseEntry.category || "",
      description: suspenseEntry.description,
      amount: suspenseEntry.amount,
      paymentMode: paymentMode,
      remark: suspenseEntry.remark || "",
      cash: suspenseEntry.cash || 0,
      bank: suspenseEntry.bank || 0,
      cashPortion: suspenseEntry.cash || 0,
      bankPortion: suspenseEntry.bank || 0,
      fine: suspenseEntry.fine || 0,
      finePaid: suspenseEntry.finePaid || 0,
    });

    // Open the transaction dialog
    setOpenDialog(true);
  };

  // Function to handle saving edited suspense entry
  const handleSubmitSuspenseEdit = async () => {
    // Similar to handleSubmitTransaction but ensures closing balances don't change
    let cashAmount, bankAmount;

    if (newTransaction.paymentMode === "split") {
      cashAmount = parseFloat(newTransaction.cashPortion) || 0;
      bankAmount = parseFloat(newTransaction.bankPortion) || 0;
    } else if (newTransaction.paymentMode === "cash") {
      cashAmount = parseFloat(newTransaction.amount) || 0;
      bankAmount = 0;
    } else if (newTransaction.paymentMode === "bank") {
      cashAmount = 0;
      bankAmount = parseFloat(newTransaction.amount) || 0;
    } else {
      cashAmount = parseFloat(newTransaction.amount) || 0;
      bankAmount = 0;
    }

    const updatedSuspenseEntry = {
      ...newTransaction,
      id: editingTransaction.id,
      date: selectedDate,
      customerSupplier: "Suspense Adjustment",
      transactionType: "suspense",
      created_at: editingTransaction.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      cash: cashAmount,
      bank: bankAmount,
      amount: parseFloat(newTransaction.amount) || 0,
      totalReceived: newTransaction.type === "income"
        ? parseFloat(newTransaction.amount) || 0
        : 0,
      totalPending: 0,
      totalPaid: newTransaction.type === "expense"
        ? parseFloat(newTransaction.amount) || 0
        : 0,
    };

    delete updatedSuspenseEntry.cashPortion;
    delete updatedSuspenseEntry.bankPortion;

    let updatedManualTransactions = { ...manualTransactions };

    if (editingTransaction.type === "income") {
      updatedManualTransactions = {
        ...updatedManualTransactions,
        income: updatedManualTransactions.income.map(t =>
          t.id === editingTransaction.id ? updatedSuspenseEntry : t
        ),
      };

      // If type changed from income to expense
      if (newTransaction.type === "expense") {
        updatedManualTransactions = {
          ...updatedManualTransactions,
          income: updatedManualTransactions.income.filter(t =>
            t.id !== editingTransaction.id
          ),
          expense: [...updatedManualTransactions.expense, updatedSuspenseEntry],
        };
      }
    } else {
      updatedManualTransactions = {
        ...updatedManualTransactions,
        expense: updatedManualTransactions.expense.map(t =>
          t.id === editingTransaction.id ? updatedSuspenseEntry : t
        ),
      };

      // If type changed from expense to income
      if (newTransaction.type === "income") {
        updatedManualTransactions = {
          ...updatedManualTransactions,
          expense: updatedManualTransactions.expense.filter(t =>
            t.id !== editingTransaction.id
          ),
          income: [...updatedManualTransactions.income, updatedSuspenseEntry],
        };
      }
    }

    // Update local state
    setManualTransactions(updatedManualTransactions);

    // Update backend while keeping original closing balances
    try {
      const allIncomeTransactions = [
        ...updatedManualTransactions.income,
        ...autoIncomeTransactions,
      ];
      const allExpenseTransactions = [
        ...updatedManualTransactions.expense,
        ...autoExpenseTransactions,
      ];

      // Calculate totals
      let cashIncome = 0;
      let bankIncome = 0;
      let cashExpense = 0;
      let bankExpense = 0;

      allIncomeTransactions.forEach((t) => {
        cashIncome += parseFloat(t.cash) || 0;
        bankIncome += parseFloat(t.bank) || 0;
      });

      allExpenseTransactions.forEach((t) => {
        cashExpense += parseFloat(t.cash) || 0;
        bankExpense += parseFloat(t.bank) || 0;
      });

      const totalIncome = cashIncome + bankIncome;
      const totalExpense = cashExpense + bankExpense;
      const netProfit = totalIncome - totalExpense;

      // Keep original closing balances
      const closingBalanceCash = selectedRoznama?.closingBalanceCash || summary.closingBalanceCash;
      const closingBalanceBank = selectedRoznama?.closingBalanceBank || summary.closingBalanceBank;

      const roznamaUpdateData = {
        date: selectedDate,
        openingBalanceCash: openingBalance.cash,
        openingBalanceBank: openingBalance.bank,
        closingBalanceCash,
        closingBalanceBank,
        incomeTransactions: allIncomeTransactions,
        expenseTransactions: allExpenseTransactions,
        netProfit,
        totalIncome,
        totalExpense,
        lastAdjusted: new Date().toISOString(),
      };

      if (selectedRoznama?.id) {
        const response = await updateRoznama(selectedRoznama.id, roznamaUpdateData);

        if (response.success) {
          showSnackbar("Suspense entry updated!", "success");
          await refresh();
          setSelectedRoznama(prev => ({
            ...prev,
            ...roznamaUpdateData,
          }));
        } else {
          showSnackbar(`Failed to update: ${response.error}`, "error");
          setManualTransactions(manualTransactions);
        }
      }
    } catch (error) {
      console.error("Error updating suspense:", error);
      showSnackbar("Error updating suspense entry", "error");
      setManualTransactions(manualTransactions);
    }

    handleCloseDialog();
  };

  const loadRoznamaForDate = useCallback(async (date) => {
    if (loadingRef.current) {
      console.log("Already loading roznama, skipping...");
      return;
    }

    // setPageLoaded(false);
    setLoadingRoznama(true);
    loadingRef.current = true;

    try {
      console.log("Loading roznama for date:", date);

      // First, check if we already have this roznama in context (synchronous check)
      const existingRoznama = roznama?.find(r => {
        try {
          const roznamaDate = convertDateToYYYYMMDD(r.date);
          const targetDate = convertDateToYYYYMMDD(date);
          return roznamaDate === targetDate;
        } catch (error) {
          return false;
        }
      });

      if (existingRoznama) {
        console.log("Found existing roznama in context:", existingRoznama);
        setSelectedRoznama(existingRoznama);
        // setPageLoaded(true);
        loadingRef.current = false;
        return;
      }

      // If not in context, check via API
      console.log(`No roznama found in context for ${date}, checking API...`);

      const result = await findRoznamaByDate(date);

      if (result?.success && result.data) {
        console.log("Found roznama via API:", result.data);
        setSelectedRoznama(result.data);
      } else {
        console.log(`No roznama found for ${date}`);

        // Only create new roznama if it's today's date
        const today = format(new Date(), "yyyy-MM-dd");
        const targetDate = convertDateToYYYYMMDD(date);

        if (targetDate === today) {
          console.log(`Creating new roznama for today (${date})...`);

          // Get last roznama for opening balances
          let lastRoznama = roznama.length > 0 ? roznama[roznama.length - 1] : null;

          const createResult = await addRoznama({
            date: date,
            openingBalanceCash: lastRoznama?.closingBalanceCash || 0,
            openingBalanceBank: lastRoznama?.closingBalanceBank || 0,
            closingBalanceCash: 0,
            closingBalanceBank: 0,
            incomeTransactions: [],
            expenseTransactions: [],
            netProfit: 0,
            totalIncome: 0,
            totalExpense: 0
          });

          if (createResult.success) {
            console.log("Roznama created successfully:", createResult.data);
            setSelectedRoznama(createResult.data);
          } else {
            console.error("Failed to create roznama:", createResult.error);
            setSelectedRoznama(null);
          }
        } else {
          console.log(`Not creating roznama for past/future date: ${date}`);
          setSelectedRoznama(null);
        }
      }
    } catch (error) {
      console.error("Error loading roznama:", error);
      setSelectedRoznama(null);
    } finally {
      // setPageLoaded(true);
      loadingRef.current = false;
      setLoadingRoznama(false);
    }
  }, [findRoznamaByDate, roznama, addRoznama]);

  const handleOpeningBalanceChange = (field, value) => {
    setOpeningBalance((prev) => ({
      ...prev,
      [field]: parseFloat(value) || 0,
    }));
  };

  // Helper function to check if a roznama day is closed
  const isDayClosed = (roznama) => {
    if (!roznama) return false;
    // const isClosed =
    // roznama.closingBalanceCash !== 0 || roznama.closingBalanceBank !== 0;
    return false;
  };

  // Add this helper function at the top of your component
  const convertDateToYYYYMMDD = (dateString) => {
    if (!dateString) return null;

    try {
      const date = new Date(dateString);

      // Check if date is valid
      // if (isNaN(date.getTime())) {
      // Try parsing as DD/MM/YYYY
      // const parts = dateString.split('/');
      // if (parts.length === 3) {
      // const day = parseInt(parts[0], 10);
      // const month = parseInt(parts[1], 10) - 1;
      // const year = parseInt(parts[2], 10);
      // const newDate = new Date(year, month, day);

      // if (!isNaN(newDate.getTime())) {
      // date = newDate;
      // }
      // }
      // }

      // Format to YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error converting date:", error);
      return null;
    }
  };

  // Load roznama for selected date
  useEffect(() => {
    console.log("Selected Date Changed:", selectedDate);

    // Check if roznama exists in context first (synchronous)
    const targetDate = convertDateToYYYYMMDD(selectedDate);
    const existingRoznama = roznama?.find(r => {
      try {
        const roznamaDate = convertDateToYYYYMMDD(r.date);
        return roznamaDate === targetDate;
      } catch (error) {
        return false;
      }
    });

    if (existingRoznama) {
      console.log("Found existing roznama in context, setting directly");
      setSelectedRoznama(existingRoznama);
      return;
    }

    const today = format(new Date(), "yyyy-MM-dd");

    if (targetDate !== today) {
      // For past/future dates with no roznama, set null immediately
      console.log("Past/future date with no roznama, setting null");
      setSelectedRoznama(null);
    } else if (!loadingRef.current) {
      // Only load for today's date if not already loading
      console.log("Loading roznama for today");
      const timer = setTimeout(() => {
        loadRoznamaForDate(selectedDate);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [selectedDate, roznama, loadRoznamaForDate]);

  useEffect(() => {
    return () => {
      loadingRef.current = false;
    };
  }, []);

  // Initialize local state for new day
  useEffect(() => {
    console.log("Selected Roznama Changed:", selectedRoznama);

    if (selectedRoznama) {
      console.log("Loading roznama data:", selectedRoznama);
      setOpeningBalance({
        cash: selectedRoznama.openingBalanceCash || 0,
        bank: selectedRoznama.openingBalanceBank || 0,
      });
      setManualTransactions({
        income: selectedRoznama.incomeTransactions || [],
        expense: selectedRoznama.expenseTransactions || [],
      });
    } else {
      const today = format(new Date(), "yyyy-MM-dd");
      if (selectedDate === today) {
        console.log("New day - resetting local state");
        setOpeningBalance({
          cash: 0,
          bank: 0,
        });
        setManualTransactions({
          income: [],
          expense: [],
        });
      } else {
        setOpeningBalance({
          cash: 0,
          bank: 0,
        });
        setManualTransactions({
          income: [],
          expense: [],
        });
      }
    }
  }, [selectedRoznama, selectedDate]);

  // const convertDMYtoISORobust = (dateString) => {
  //   if (!dateString) return null;
  //   dateString =
  //     dateString.charAt(0) === "'"
  //       ? dateString.trim().slice(1)
  //       : dateString.trim();
  //   const delimiters = /[/\-.]/;
  //   const parts = dateString.split(delimiters);

  //   if (parts.length !== 3) return null;

  //   const day = parseInt(parts[0], 10);
  //   const month = parseInt(parts[1], 10);
  //   const year = parseInt(parts[2], 10);

  //   if (parts[0].length === 4) {
  //     return dateString;
  //   } else if (month > 12 && day <= 12) {
  //     return `${year}-${parts[0].padStart(2, "0")}-${parts[1].padStart(
  //       2,
  //       "0"
  //     )}`;
  //   } else {
  //     return `${year}-${parts[1].padStart(2, "0")}-${parts[0].padStart(
  //       2,
  //       "0"
  //     )}`;
  //   }
  // };

  const { autoIncomeTransactions, autoExpenseTransactions } = { autoIncomeTransactions: [], autoExpenseTransactions: [] };

  // Process customer and supplier transactions for the selected date
  // const { autoIncomeTransactions, autoExpenseTransactions } = useMemo(() => {
  // const selectedDateObj = parseISO(selectedDate);
  // const selectedDayStart = startOfDay(selectedDateObj);
  // const selectedDayEnd = endOfDay(selectedDateObj);

  // Process customer transactions (auto income)
  // const customerTrans = customerTransactions
  //   .filter((transaction) => {
  //     try {
  //       const transactionDate = convertDMYtoISORobust(transaction.date);
  //       return isWithinInterval(transactionDate, {
  //         start: selectedDayStart,
  //         end: selectedDayEnd,
  //       });
  //     } catch (error) {
  //       return false;
  //     }
  //   })
  //   .map((transaction) => {
  //     console.log(transaction)
  //     const customer = customers.find((c) => c.id === transaction.customer);
  //     const summary = transaction.summary || {};
  //     const paymentDetails = transaction.paymentDetails || {};
  //     const items = transaction.items || [];
  //     const cashPayment = parseInt(parseFloat(transaction?.paymentDetails?.cash).toFixed(2)) + parseInt(parseFloat(paymentDetails?.advancePayment?.breakup?.cash).toFixed(2))
  //     const bankPayment =
  //       parseInt(parseFloat(paymentDetails.cheque || 0).toFixed(2)) +
  //       parseInt(parseFloat(paymentDetails.online || 0).toFixed(2)) +
  //       parseInt(parseFloat(paymentDetails?.advancePayment?.breakup?.online).toFixed(2));
  //     const paymentMode = bankPayment === 0 && cashPayment > 0 ? "cash" : bankPayment > 0 && cashPayment === 0 ? "bank" : "split"
  //     return {
  //       id: transaction.id,
  //       date: transaction.date,
  //       type: "income",
  //       billNumber: `CUST-${transaction.transactionDetails?.billNo}`,
  //       customerSupplier: customer ? customer.name : "Unknown Customer",
  //       description:
  //         items
  //           .map((item) => item.itemName || item.name || "Item")
  //           .join(", ") || "Customer Sale",
  //       amount: summary.grandTotal || 0,
  //       totalReceived: transaction?.paymentDetails?.totalPaymentDone || 0,
  //       bank: bankPayment || 0,
  //       cash: cashPayment || 0,
  //       totalPending: parseFloat(summary?.pendingAmount || 0).toFixed(0),
  //       paymentMode: paymentMode,
  //       remark: "Auto-generated from customer transaction",
  //       transactionType: "customer",
  //       isAutoGenerated: true,
  //       referenceId: transaction.id,
  //     };
  //   });

  // Process supplier transactions (auto expense)
  // const supplierTrans = supplierTransactions
  //   .filter((transaction) => {
  //     try {
  //       const transactionDate = parseISO(transaction.date);
  //       return isWithinInterval(transactionDate, {
  //         start: selectedDayStart,
  //         end: selectedDayEnd,
  //       });
  //     } catch (error) {
  //       return false;
  //     }
  //   })
  //   .map((transaction) => {
  //     const supplier = suppliers.find((s) => s.id === transaction.supplier);
  //     const summary = transaction.summary || {};
  //     const items = transaction.items || [];
  //     let bank = parseInt(transaction?.payments?.bank);
  //     let cash = parseInt(transaction?.payments?.cash);
  //     const paymentMode = bank > 0 && cash === 0 ? "bank" : bank === 0 && cash > 0 ? "cash" : "split"

  //     return {
  //       id: transaction.id,
  //       date: transaction.date,
  //       type: "expense",
  //       billNumber: `SUPP-${transaction.id.substring(0, 8)}`,
  //       customerSupplier: supplier ? supplier.name : "Unknown Supplier",
  //       description:
  //         items
  //           .map((item) => item.itemName || item.name || "Item")
  //           .join(", ") || "Supplier Purchase",
  //       amount: summary.grandTotal || 0,
  //       totalPaid: transaction?.summary?.paymentDone || 0,
  //       fine: transaction?.summary?.fineBalance || 0,
  //       bank: transaction?.payments?.bank || 0,
  //       cash: transaction?.payments?.cash || 0,
  //       finePaid: transaction?.summary?.finePayment || 0,
  //       totalPending: parseFloat(summary?.pendingAmount || 0).toFixed(0),
  //       paymentMode: paymentMode,
  //       remark: "Auto-generated from supplier transaction",
  //       transactionType: "supplier",
  //       isAutoGenerated: true,
  //       referenceId: transaction.id,
  //     };
  //   });
  //   return {
  //     autoIncomeTransactions: [],
  //     autoExpenseTransactions: [],
  //   };
  // }, [
  //   selectedDate,
  // ]);

  // Check if it's a past day (read-only) OR if day is closed
  const isPastDay = useCallback(() => {
    const today = new Date();
    const selected = parseISO(selectedDate);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const todayStr = formatDate(today);
    const selectedStr = formatDate(selected);

    const isPast = selectedStr < todayStr;
    const isDayClosedCheck = isDayClosed(selectedRoznama);

    return isPast || isDayClosedCheck;
  }, [selectedDate, selectedRoznama]);

  // Combine manual and auto-generated transactions
  const allIncomeTransactions = useMemo(() => {
    console.log("Close Roznama Day:", isDayClosed(selectedRoznama));
    if (isDayClosed(selectedRoznama) || isPastDay()) {
      return [...manualTransactions.income];
    }
    return [...manualTransactions.income, ...autoIncomeTransactions];
  }, [manualTransactions.income, autoIncomeTransactions, selectedRoznama, isPastDay]);

  const allExpenseTransactions = useMemo(() => {
    if (isDayClosed(selectedRoznama) || isPastDay()) {
      return [...manualTransactions.expense];
    }
    return [...manualTransactions.expense, ...autoExpenseTransactions];
  }, [manualTransactions.expense, autoExpenseTransactions, selectedRoznama, isPastDay]);

  const summary = useMemo(() => {
    const openingBalanceCash = openingBalance.cash || 0;
    const openingBalanceBank = openingBalance.bank || 0;

    let cashIncome = 0;
    let bankIncome = 0;
    let cashExpense = 0;
    let bankExpense = 0;

    allIncomeTransactions.forEach((t) => {
      const cash = parseFloat(t.cash) || 0;
      const bank = parseFloat(t.bank) || 0;
      cashIncome += cash;
      bankIncome += bank;
    });

    allExpenseTransactions.forEach((t) => {
      const cash = parseFloat(t.cash) || 0;
      const bank = parseFloat(t.bank) || 0;
      cashExpense += cash;
      bankExpense += bank;
    });


    // let autoIncomeCount = 0;
    // let autoExpenseCount = 0;
    // if (!selectedRoznama) {
    // autoIncomeCount = autoIncomeTransactions.length;
    // autoExpenseCount = autoExpenseTransactions.length;
    // }

    const totalIncome = cashIncome + bankIncome;
    const totalExpense = cashExpense + bankExpense;
    const netProfit = totalIncome - totalExpense;

    const closingBalanceCash = openingBalanceCash + cashIncome - cashExpense;
    const closingBalanceBank = openingBalanceBank + bankIncome - bankExpense;
    const totalClosingBalance = closingBalanceCash + closingBalanceBank;

    return {
      openingBalanceCash,
      openingBalanceBank,
      cashIncome,
      bankIncome,
      cashExpense,
      bankExpense,
      totalIncome,
      totalExpense,
      netProfit,
      closingBalanceCash,
      closingBalanceBank,
      totalClosingBalance,
      manualIncomeCount: manualTransactions.income.length,
      manualExpenseCount: manualTransactions.expense.length,
      autoIncomeCount: autoIncomeTransactions.length,
      autoExpenseCount: autoExpenseTransactions.length,
      totalTransactions:
        allIncomeTransactions.length + allExpenseTransactions.length,
    };
  }, [
    openingBalance,
    allIncomeTransactions,
    allExpenseTransactions,
    manualTransactions,
    autoIncomeTransactions,
    autoExpenseTransactions,
  ]);

  // Table pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Sort function
  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // Get current transactions based on active tab
  const getCurrentTransactions = () => {
    const transactions =
      activeTab === 0 ? allIncomeTransactions : allExpenseTransactions;
    return stableSort(transactions, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  };

  const generateDailyReportData = () => {
    // Calculate detailed breakdown
    const incomeBreakdown = {
      cash: 0,
      bank: 0,
      split: 0,
      cashTransactions: [],
      bankTransactions: [],
      splitTransactions: [],
    };

    const expenseBreakdown = {
      cash: 0,
      bank: 0,
      split: 0,
      cashTransactions: [],
      bankTransactions: [],
      splitTransactions: [],
    };

    // Process income transactions
    allIncomeTransactions.forEach((transaction) => {
      const cashAmount = parseFloat(transaction.cash) || 0;
      const bankAmount = parseFloat(transaction.bank) || 0;

      if (cashAmount > 0 && bankAmount > 0) {
        incomeBreakdown.split += transaction.amount;
        incomeBreakdown.splitTransactions.push({
          ...transaction,
          cashAmount,
          bankAmount,
        });
      } else if (cashAmount > 0) {
        incomeBreakdown.cash += transaction.amount;
        incomeBreakdown.cashTransactions.push({
          ...transaction,
          cashAmount,
          bankAmount,
        });
      } else if (bankAmount > 0) {
        incomeBreakdown.bank += transaction.amount;
        incomeBreakdown.bankTransactions.push({
          ...transaction,
          cashAmount,
          bankAmount,
        });
      }
    });

    // Process expense transactions
    allExpenseTransactions.forEach((transaction) => {
      const cashAmount = parseFloat(transaction.cash) || 0;
      const bankAmount = parseFloat(transaction.bank) || 0;

      if (cashAmount > 0 && bankAmount > 0) {
        expenseBreakdown.split += transaction.amount;
        expenseBreakdown.splitTransactions.push({
          ...transaction,
          cashAmount,
          bankAmount,
        });
      } else if (cashAmount > 0) {
        expenseBreakdown.cash += transaction.amount;
        expenseBreakdown.cashTransactions.push({
          ...transaction,
          cashAmount,
          bankAmount,
        });
      } else if (bankAmount > 0) {
        expenseBreakdown.bank += transaction.amount;
        expenseBreakdown.bankTransactions.push({
          ...transaction,
          cashAmount,
          bankAmount,
        });
      }
    });

    return {
      date: selectedDate,
      openingBalanceCash: summary.openingBalanceCash,
      openingBalanceBank: summary.openingBalanceBank,
      income: incomeBreakdown,
      expense: expenseBreakdown,
      summary: {
        totalIncomeCash: summary.cashIncome,
        totalIncomeBank: summary.bankIncome,
        totalIncome: summary.totalIncome,
        totalExpenseCash: summary.cashExpense,
        totalExpenseBank: summary.bankExpense,
        totalExpense: summary.totalExpense,
        netProfit: summary.netProfit,
        closingBalanceCash: summary.closingBalanceCash,
        closingBalanceBank: summary.closingBalanceBank,
        totalClosingBalance: summary.totalClosingBalance,
      },
    };
  };

  // Add this function to handle opening the daily report
  const handleOpenDailyReport = () => {
    setDailyReportData(generateDailyReportData());
    setShowDailyReport(true);
  };

  // Table headers
  const incomeHeaders = [
    { id: "transactionType", label: "Type", minWidth: 100, maxWidth: 120 },
    { id: "billNumber", label: "Bill No.", minWidth: 140, maxWidth: 200 },
    { id: "customerSupplier", label: "Customer", minWidth: 180, maxWidth: 250 },
    { id: "description", label: "Description", minWidth: 200, maxWidth: 400 },
    {
      id: "amount",
      label: "Amount",
      minWidth: 140,
      maxWidth: 160,
      align: "right",
    },
    {
      id: "totalReceived",
      label: "Received",
      minWidth: 140,
      maxWidth: 160,
      align: "right",
    },
    {
      id: "totalPending",
      label: "Pending",
      minWidth: 140,
      maxWidth: 160,
      align: "right",
    },
    { id: "bank", label: "Bank", minWidth: 140, maxWidth: 160, align: "right" },
    { id: "cash", label: "Cash", minWidth: 140, maxWidth: 160, align: "right" },
    { id: "paymentMode", label: "Mode", minWidth: 120, maxWidth: 150 },
    {
      id: "actions",
      label: "Actions",
      minWidth: 140,
      maxWidth: 160,
      sortable: false,
    },
  ];

  const expenseHeaders = [
    { id: "transactionType", label: "Type", minWidth: 100, maxWidth: 120 },
    { id: "billNumber", label: "Bill No.", minWidth: 140, maxWidth: 200 },
    { id: "customerSupplier", label: "Supplier", minWidth: 180, maxWidth: 250 },
    { id: "description", label: "Description", minWidth: 200, maxWidth: 400 },
    { id: "fine", label: "Fine", minWidth: 120, maxWidth: 140, align: "right" },
    {
      id: "amount",
      label: "Amount",
      minWidth: 140,
      maxWidth: 160,
      align: "right",
    },
    {
      id: "totalPaid",
      label: "Paid",
      minWidth: 140,
      maxWidth: 160,
      align: "right",
    },
    {
      id: "totalPending",
      label: "Pending",
      minWidth: 140,
      maxWidth: 160,
      align: "right",
    },
    { id: "bank", label: "Bank", minWidth: 140, maxWidth: 160, align: "right" },
    { id: "cash", label: "Cash", minWidth: 140, maxWidth: 160, align: "right" },
    {
      id: "finePaid",
      label: "Fine Paid",
      minWidth: 140,
      maxWidth: 160,
      align: "right",
    },
    { id: "paymentMode", label: "Mode", minWidth: 120, maxWidth: 150 },
    {
      id: "actions",
      label: "Actions",
      minWidth: 140,
      maxWidth: 160,
      sortable: false,
    },
  ];

  const currentHeaders = activeTab === 0 ? incomeHeaders : expenseHeaders;
  const currentTransactions = getCurrentTransactions();

  // Add new transaction (local only)
  const handleOpenDialog = (type = "income") => {
    const billPrefix = type === "income" ? "MAN-INV" : "MAN-EXP";
    const manualTransactionsList =
      type === "income"
        ? manualTransactions.income
        : manualTransactions.expense;

    const lastBill = manualTransactionsList
      .filter((t) => t.billNumber && t.billNumber.startsWith(billPrefix))
      .sort((a, b) => {
        const aNum = parseInt(a.billNumber.split("-")[2]) || 0;
        const bNum = parseInt(b.billNumber.split("-")[2]) || 0;
        return bNum - aNum;
      })[0];

    let nextNumber = 1;
    if (lastBill && lastBill.billNumber) {
      const lastNum = parseInt(lastBill.billNumber.split("-")[2]);
      nextNumber = isNaN(lastNum) ? 1 : lastNum + 1;
    }

    setNewTransaction({
      type,
      billNumber: `${billPrefix}-${String(nextNumber).padStart(3, "0")}`,
      category: "",
      description: "",
      amount: "",
      paymentMode: "cash",
      remark: "",
      cash: "",
      bank: "",
      cashPortion: "",
      bankPortion: "",
      fine: "",
      finePaid: "",
    });
    setEditingTransaction(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTransaction(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setNewTransaction((prev) => ({
      ...prev,
      [name]: type === "number" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmitTransaction = async () => {
    if (editingTransaction?.transactionType === "suspense") {
      await handleSubmitSuspenseEdit();
      return;
    }
    let cashAmount, bankAmount;

    if (newTransaction.paymentMode === "split") {
      cashAmount = parseFloat(newTransaction.cashPortion) || 0;
      bankAmount = parseFloat(newTransaction.bankPortion) || 0;
    } else if (newTransaction.paymentMode === "cash") {
      cashAmount = parseFloat(newTransaction.amount) || 0;
      bankAmount = 0;
    } else if (newTransaction.paymentMode === "bank") {
      cashAmount = 0;
      bankAmount = parseFloat(newTransaction.amount) || 0;
    } else {
      cashAmount = parseFloat(newTransaction.amount) || 0;
      bankAmount = 0;
    }

    const transactionWithId = {
      ...newTransaction,
      id: `manual-${Date.now()}`,
      date: selectedDate,
      customerSupplier:
        newTransaction.type === "income"
          ? "Daybook Customer"
          : "Daybook Supplier",
      transactionType: "manual",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      cash: cashAmount,
      bank: bankAmount,
      amount: parseFloat(newTransaction.amount) || 0,
      totalReceived:
        newTransaction.type === "income"
          ? parseFloat(newTransaction.amount) || 0
          : 0,
      totalPending: 0,
      totalPaid:
        newTransaction.type === "expense"
          ? parseFloat(newTransaction.amount) || 0
          : 0,
      fine:
        newTransaction.type === "expense"
          ? parseFloat(newTransaction.fine) || 0
          : 0,
      finePaid:
        newTransaction.type === "expense"
          ? parseFloat(newTransaction.finePaid) || 0
          : 0,
    };

    delete transactionWithId.cashPortion;
    delete transactionWithId.bankPortion;

    let updatedManualTransactions = { ...manualTransactions };

    if (editingTransaction) {
      if (newTransaction.type === "income") {
        updatedManualTransactions = {
          ...updatedManualTransactions,
          income: updatedManualTransactions.income.map((t) =>
            t.id === editingTransaction.id ? transactionWithId : t
          ),
        };
      } else {
        updatedManualTransactions = {
          ...updatedManualTransactions,
          expense: updatedManualTransactions.expense.map((t) =>
            t.id === editingTransaction.id ? transactionWithId : t
          ),
        };
      }
      showSnackbar("Transaction updated!", "success");
    } else {
      if (newTransaction.type === "income") {
        updatedManualTransactions = {
          ...updatedManualTransactions,
          income: [...updatedManualTransactions.income, transactionWithId],
        };
      } else {
        updatedManualTransactions = {
          ...updatedManualTransactions,
          expense: [...updatedManualTransactions.expense, transactionWithId],
        };
      }
      showSnackbar("Transaction added!", "success");
    }

    // Set manual transactions first
    setManualTransactions(updatedManualTransactions);

    // Calculate updated closing balances
    const calculateUpdatedClosingBalances = () => {
      // Get opening balances
      const openingCash = openingBalance.cash || 0;
      const openingBank = openingBalance.bank || 0;

      // Combine manual and auto transactions for calculation
      const allIncomeTrans = [...updatedManualTransactions.income, ...autoIncomeTransactions];
      const allExpenseTrans = [...updatedManualTransactions.expense, ...autoExpenseTransactions];

      // Calculate income
      let cashIncome = 0;
      let bankIncome = 0;
      allIncomeTrans.forEach((t) => {
        cashIncome += parseFloat(t.cash) || 0;
        bankIncome += parseFloat(t.bank) || 0;
      });

      // Calculate expense
      let cashExpense = 0;
      let bankExpense = 0;
      allExpenseTrans.forEach((t) => {
        cashExpense += parseFloat(t.cash) || 0;
        bankExpense += parseFloat(t.bank) || 0;
      });

      // Calculate totals
      const totalIncome = cashIncome + bankIncome;
      const totalExpense = cashExpense + bankExpense;
      const netProfit = totalIncome - totalExpense;

      // Calculate closing balances
      const closingBalanceCash = openingCash + cashIncome - cashExpense;
      const closingBalanceBank = openingBank + bankIncome - bankExpense;

      return {
        openingBalanceCash: openingCash,
        openingBalanceBank: openingBank,
        closingBalanceCash,
        closingBalanceBank,
        totalIncome,
        totalExpense,
        netProfit,
        incomeTransactions: allIncomeTrans,
        expenseTransactions: allExpenseTrans
      };
    };

    // Calculate the new closing balances
    const updatedRoznamaData = calculateUpdatedClosingBalances();

    try {
      // Prepare backend data with updated closing balances
      const backendData = {
        date: selectedDate,
        openingBalanceCash: updatedRoznamaData.openingBalanceCash,
        openingBalanceBank: updatedRoznamaData.openingBalanceBank,
        closingBalanceCash: updatedRoznamaData.closingBalanceCash,
        closingBalanceBank: updatedRoznamaData.closingBalanceBank,
        incomeTransactions: updatedRoznamaData.incomeTransactions,
        expenseTransactions: updatedRoznamaData.expenseTransactions,
        netProfit: updatedRoznamaData.netProfit,
        totalIncome: updatedRoznamaData.totalIncome,
        totalExpense: updatedRoznamaData.totalExpense
      };

      console.log("Saving roznama with updated closing balances:", backendData);

      // Check if we're updating or creating a new roznama
      let response;
      if (selectedRoznama?.id) {
        // Update existing roznama
        response = await updateRoznama(selectedRoznama.id, backendData);
      } else {
        response = await addRoznama({
          ...backendData
        });
      }

      if (response.success) {
        console.log("Roznama saved successfully with updated balances");
        // Refresh data from backend
        await refresh();

        // Update local summary state to reflect changes
        setSelectedRoznama(prev => ({
          ...prev,
          ...backendData,
          // id: selectedRoznama?.id || response.data?.id || newId
        }));

        showSnackbar("Transaction saved successfully with updated balances!", "success");
      } else {
        console.error("Failed to save roznama:", response.error);
        showSnackbar(`Failed to save: ${response.error}`, "error");
      }
    } catch (error) {
      console.error("Error saving to backend:", error);
      showSnackbar("Transaction saved locally only", "warning");
    }

    handleCloseDialog();
  };

  // Edit transaction
  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);

    let paymentMode = "cash";
    if (transaction.bank > 0 && transaction.cash > 0) {
      paymentMode = "split";
    } else if (transaction.bank > 0) {
      paymentMode = "bank";
    }

    setNewTransaction({
      type: transaction.type,
      billNumber: transaction.billNumber,
      category: transaction.category || "",
      description: transaction.description,
      amount: transaction.amount,
      paymentMode: paymentMode,
      remark: transaction.remark || "",
      cash: transaction.cash || 0,
      bank: transaction.bank || 0,
      cashPortion: transaction.cash || 0,
      bankPortion: transaction.bank || 0,
      fine: transaction.fine || 0,
      finePaid: transaction.finePaid || 0,
    });
    setOpenDialog(true);
  };

  // Delete transaction
  const handleDeleteTransaction = async (transaction) => {
    if (window.confirm("Delete this transaction?")) {
      try {
        // First, update local state immediately for better UX
        let updatedManualTransactions;

        if (transaction.type === "income") {
          updatedManualTransactions = {
            ...manualTransactions,
            income: manualTransactions.income.filter((t) => t.id !== transaction.id),
          };
        } else {
          updatedManualTransactions = {
            ...manualTransactions,
            expense: manualTransactions.expense.filter((t) => t.id !== transaction.id),
          };
        }

        // Update local state
        setManualTransactions(updatedManualTransactions);

        // Now prepare the full roznama update
        if (selectedRoznama?.id) {
          // Combine manual and auto transactions
          const allIncomeTransactions = [
            ...updatedManualTransactions.income,
            ...autoIncomeTransactions,
          ];
          const allExpenseTransactions = [
            ...updatedManualTransactions.expense,
            ...autoExpenseTransactions,
          ];

          // Recalculate balances
          let cashIncome = 0;
          let bankIncome = 0;
          let cashExpense = 0;
          let bankExpense = 0;

          allIncomeTransactions.forEach((t) => {
            cashIncome += parseFloat(t.cash) || 0;
            bankIncome += parseFloat(t.bank) || 0;
          });

          allExpenseTransactions.forEach((t) => {
            cashExpense += parseFloat(t.cash) || 0;
            bankExpense += parseFloat(t.bank) || 0;
          });

          const totalIncome = cashIncome + bankIncome;
          const totalExpense = cashExpense + bankExpense;
          const netProfit = totalIncome - totalExpense;

          const closingBalanceCash =
            (selectedRoznama.openingBalanceCash || 0) + cashIncome - cashExpense;
          const closingBalanceBank =
            (selectedRoznama.openingBalanceBank || 0) + bankIncome - bankExpense;

          // Prepare the full roznama update
          const roznamaUpdateData = {
            date: selectedRoznama.date,
            openingBalanceCash: selectedRoznama.openingBalanceCash || 0,
            openingBalanceBank: selectedRoznama.openingBalanceBank || 0,
            closingBalanceCash,
            closingBalanceBank,
            incomeTransactions: allIncomeTransactions,
            expenseTransactions: allExpenseTransactions,
            netProfit,
            totalIncome,
            totalExpense,
          };
          // Update backend
          const response = await updateRoznama(selectedRoznama.id, roznamaUpdateData);

          if (response.success) {
            showSnackbar("Transaction deleted successfully!", "success");
            // Optionally refresh data
            await refresh();
          } else {
            showSnackbar(`Failed to delete: ${response.error}`, "error");
            // Revert local state if backend update fails
            setManualTransactions(manualTransactions);
          }
        } else {
          showSnackbar("Transaction deleted locally!", "info");
        }

      } catch (error) {
        console.error("Error deleting transaction:", error);
        showSnackbar("Error deleting transaction", "error");
        // Revert on error
        setManualTransactions(manualTransactions);
      }
    }
  };

  // View source transaction
  // const handleViewSource = (transaction) => {
  //   if (transaction.transactionType === "customer") {
  //     alert(
  //       `Customer Transaction:\nID: ${transaction.id}\nAmount: ₹${transaction.amount}`
  //     );
  //   } else if (transaction.transactionType === "supplier") {
  //     alert(
  //       `Supplier Transaction:\nID: ${transaction.id}\nAmount: ₹${transaction.amount}`
  //     );
  //   }
  // };

  // Export to CSV
  const handleExport = () => {
    let csvContent = "data:text/csv;charset=utf-8,";

    csvContent += "JEWELLERY DAYBOOK REPORT\n";
    csvContent += `Date: ${selectedDate}\n\n`;

    csvContent += "OPENING BALANCE\n";
    csvContent += `Cash: ₹${summary.openingBalanceCash.toLocaleString(
      "en-IN"
    )}\n`;
    csvContent += `Bank: ₹${summary.openingBalanceBank.toLocaleString(
      "en-IN"
    )}\n\n`;

    csvContent += "INCOME TRANSACTIONS\n";
    csvContent +=
      "Type,Bill No.,Customer,Description,Amount,Received,Pending,Bank Payment,Cash Payment,Mode,Remark\n";

    allIncomeTransactions.forEach((t) => {
      csvContent += `${t.transactionType},${t.billNumber},"${t.customerSupplier
        }","${t.description}",${t.amount},${t.totalReceived || 0},${t.totalPending || 0
        },${t.bank || 0},${t.cash || 0},${t.paymentMode},"${t.remark || ""}"\n`;
    });

    csvContent += `\nTotal Income: ₹${summary.totalIncome.toLocaleString(
      "en-IN"
    )}\n\n`;

    csvContent += "EXPENSE TRANSACTIONS\n";
    csvContent +=
      "Type,Bill No.,Supplier,Description,Fine,Amount,Paid,Pending,Bank,Cash,Fine Paid,Mode,Remark\n";

    allExpenseTransactions.forEach((t) => {
      csvContent += `${t.transactionType},${t.billNumber},"${t.customerSupplier
        }","${t.description}",${t.fine || 0},${t.amount},${t.totalPaid || 0},${t.totalPending || 0
        },${t.bank || 0},${t.cash || 0},${t.finePaid || 0},${t.paymentMode},"${t.remark || ""
        }"\n`;
    });

    csvContent += `\nTotal Expense: ₹${summary.totalExpense.toLocaleString(
      "en-IN"
    )}\n\n`;

    csvContent += "CLOSING BALANCE\n";
    csvContent += `Cash: ₹${summary.closingBalanceCash.toLocaleString(
      "en-IN"
    )}\n`;
    csvContent += `Bank: ₹${summary.closingBalanceBank.toLocaleString(
      "en-IN"
    )}\n`;
    csvContent += `Total: ₹${summary.totalClosingBalance.toLocaleString(
      "en-IN"
    )}\n\n`;

    csvContent += "SUMMARY\n";
    csvContent += `Net Profit/Loss: ₹${summary.netProfit.toLocaleString(
      "en-IN"
    )}\n`;
    csvContent += `Total Transactions: ${allIncomeTransactions.length + allExpenseTransactions.length
      }\n`;

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Daybook_${selectedDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Show snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Quick add common transactions
  const quickAddTransaction = (type, description, amount) => {
    const billPrefix = type === "income" ? "MAN-INV" : "MAN-EXP";
    const manualTransactionsList =
      type === "income"
        ? manualTransactions.income
        : manualTransactions.expense;

    const lastBill = manualTransactionsList
      .filter((t) => t.billNumber && t.billNumber.startsWith(billPrefix))
      .sort((a, b) => {
        const aNum = parseInt(a.billNumber.split("-")[2]) || 0;
        const bNum = parseInt(b.billNumber.split("-")[2]) || 0;
        return bNum - aNum;
      })[0];

    let nextNumber = 1;
    if (lastBill && lastBill.billNumber) {
      const lastNum = parseInt(lastBill.billNumber.split("-")[2]);
      nextNumber = isNaN(lastNum) ? 1 : lastNum + 1;
    }

    setNewTransaction({
      type,
      billNumber: `${billPrefix}-${String(nextNumber).padStart(3, "0")}`,
      category:
        type === "income" ? "Miscellaneous Income" : "Miscellaneous Expense",
      description,
      amount: amount,
      paymentMode: "cash",
      remark: "Quick entry",
      cash: amount,
      bank: 0,
      cashPortion: amount,
      bankPortion: 0,
      ...(type === "income"
        ? {
          totalReceived: amount,
          totalPending: 0,
        }
        : {
          totalPaid: amount,
          totalPending: 0,
          fine: 0,
          finePaid: 0,
        }),
    });
    setEditingTransaction(null);
    setOpenDialog(true);
  };

  const handleRefresh = async () => {
    setIsPageLoading(true);
    setLoadingRoznama(true);
    try {
      await refresh();
      await loadRoznamaForDate(selectedDate);
      showSnackbar("Data refreshed!", "info");
    } catch (error) {
      console.error("Refresh error:", error);
    } finally {
      setTimeout(() => {
        setIsPageLoading(false);
        setLoadingRoznama(false);
      }, 300); // Small delay to show skeleton
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return `₹${(parseFloat(value) || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  // Format fine
  const formatFine = (value) => {
    return `${(parseFloat(value) || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} gm`;
  };
  console.log(loading, !selectedRoznama, isPastDay(), loadingRoznama)
  // if (loading) {
  //   return (
  //     <Box
  //       sx={{
  //         minHeight: "100vh",
  //         display: "flex",
  //         alignItems: "center",
  //         justifyContent: "center",
          
  //       }}
  //     >
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  return (
    <Box
      sx={{
        p: 2
      }}
    >
      {isClosingDay && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(10px)",
          }}
        >
          <Card
            sx={{
              p: 4,
              maxWidth: 400,
              textAlign: "center",
              background: isDark
                ? "rgba(30, 41, 59, 0.9)"
                : "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              borderRadius: "20px",
              border: `1px solid ${alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
            }}
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography
              variant="h6"
              sx={{ color: isDark ? "#f1f5f9" : "#1e293b" }}
            >
              Closing Day...
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1, color: isDark ? "#94a3b8" : "#64748b" }}
            >
              Saving all transactions and calculating balances
            </Typography>
          </Card>
        </Box>
      )}
      {isPageLoading ? (
        <Container maxWidth="lg">
          {/* Header Skeleton */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                p: 3,
                borderRadius: "20px",
                backgroundColor: 'transparent',
                border: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Skeleton variant="rounded" width={60} height={60} sx={{ borderRadius: "16px", mr: 2, }} />
                <Box>
                  <Skeleton variant="text" width={250} height={40} sx={{ mb: 0.5,  }} />
                  <Skeleton variant="text" width={200} height={24} />
                </Box>
              </Box>
              <Stack direction="row" spacing={2}>
                <Skeleton variant="rounded" width={100} height={40} sx={{ borderRadius: "12px",}} />
                <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: "12px",}} />
                <Skeleton variant="rounded" width={100} height={40} sx={{ borderRadius: "12px",}} />
              </Stack>
            </Box>

            {/* Grid Skeletons */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <DateSelectorSkeleton />
              </Grid>
              <Grid item xs={12} md={3}>
                <OpeningBalanceSkeleton />
              </Grid>
              <Grid item xs={12} md={3}>
                <TransactionStatsSkeleton />
              </Grid>
              <Grid item xs={12} md={3}>
                <DayActionsSkeleton />
              </Grid>
            </Grid>

            {/* Quick Add Skeleton */}
            <QuickAddSkeleton />

            {/* Summary Cards Skeletons */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <SummaryCardSkeleton />
              </Grid>
              <Grid item xs={12} md={6}>
                <SummaryCardSkeleton />
              </Grid>
              <Grid item xs={12}>
                <BalancePositionSkeleton />
              </Grid>
            </Grid>

            {/* Table Skeleton */}
            <TableSkeleton />
          </Box>
        </Container>
      ) : (

      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              p: 3,
              borderRadius: "20px",
              background: isDark
                ? "linear-gradient(90deg, rgba(96, 165, 250, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)"
                : "linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.04) 100%)",
              border: `1px solid ${alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: isDark
                    ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
                    : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                  boxShadow: isDark
                    ? "0 8px 32px rgba(96, 165, 250, 0.3)"
                    : "0 8px 32px rgba(37, 99, 235, 0.3)",
                  mr: 2,
                }}
              >
                <DiamondIcon sx={{ fontSize: 32, color: "white" }} />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: isDark ? "#f1f5f9" : "#1e293b",
                    mb: 0.5,
                  }}
                >
                  Jewellery Daybook
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: isDark ? "#94a3b8" : "#64748b",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CalendarToday sx={{ fontSize: 16 }} />
                  {isDayClosed(selectedRoznama)
                    ? "Viewing Closed Day"
                    : isPastDay()
                      ? "Viewing Past Day"
                      : "Daily Transaction Tracker"}{" "}
                  - {format(parseISO(selectedDate), "dd/MM/yyyy")}
                  {selectedRoznama?.id && (
                    <Chip
                      label={isDayClosed(selectedRoznama) ? "Closed" : "Saved"}
                      size="small"
                      sx={{
                        mr: 1,
                        backgroundColor: isDayClosed(selectedRoznama)
                          ? isDark
                            ? "rgba(251, 191, 36, 0.15)"
                            : "rgba(251, 191, 36, 0.15)"
                          : isDark
                            ? "rgba(34, 197, 94, 0.15)"
                            : "rgba(34, 197, 94, 0.15)",
                        color: isDayClosed(selectedRoznama)
                          ? isDark
                            ? "#f59e0b"
                            : "#f59e0b"
                          : isDark
                            ? "#10b981"
                            : "#10b981",
                        border: `1px solid ${alpha(
                          isDayClosed(selectedRoznama) ? "#f59e0b" : "#10b981",
                          0.3
                        )}`,
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ borderRadius: "12px" }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<SummarizeIcon />}
                onClick={handleOpenDailyReport}
                sx={{ borderRadius: "12px" }}
              >
                Daily Report
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                sx={{ borderRadius: "12px" }}
              >
                Export
              </Button>
              {!isPastDay() && !isDayClosed(selectedRoznama) && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog("income")}
                    sx={{
                      borderRadius: "12px",
                      background: isDark
                        ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                        : "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                      boxShadow: isDark
                        ? "0 8px 25px rgba(16, 185, 129, 0.3)"
                        : "0 8px 25px rgba(16, 185, 129, 0.3)",
                    }}
                  >
                    New Income
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog("expense")}
                    sx={{
                      borderRadius: "12px",
                      background: isDark
                        ? "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
                        : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                      boxShadow: isDark
                        ? "0 8px 25px rgba(239, 68, 68, 0.3)"
                        : "0 8px 25px rgba(239, 68, 68, 0.3)",
                      fontSize: "15px",
                    }}
                  >
                    New Expense
                  </Button>
                </>
              )}
            </Stack>
          </Box>

          {/* Date Selector and Controls */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  background: isDark
                    ? "rgba(30, 41, 59, 0.7)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha(
                    isDark ? "#475569" : "#e2e8f0",
                    isDark ? 0.2 : 0.3
                  )}`,
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  height: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: isDark ? "#f1f5f9" : "#1e293b",
                    fontWeight: 600,
                  }}
                >
                  Date Selection
                </Typography>
                <TextField
                  fullWidth
                  label="Select Date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                    },
                  }}
                />
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() =>
                    setSelectedDate(format(new Date(), "yyyy-MM-dd"))
                  }
                  sx={{ mt: 2, borderRadius: "12px" }}
                >
                  Today
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  background: isDark
                    ? "rgba(30, 41, 59, 0.7)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha(
                    isDark ? "#475569" : "#e2e8f0",
                    isDark ? 0.2 : 0.3
                  )}`,
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  height: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: isDark ? "#f1f5f9" : "#1e293b",
                    fontWeight: 600,
                  }}
                >
                  Opening Balance
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Cash Opening"
                    type="number"
                    value={openingBalance.cash}
                    onChange={(e) =>
                      handleOpeningBalanceChange("cash", e.target.value)
                    }
                    size="small"
                    disabled={isPastDay()}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Bank Opening"
                    type="number"
                    value={openingBalance.bank}
                    onChange={(e) =>
                      handleOpeningBalanceChange("bank", e.target.value)
                    }
                    size="small"
                    disabled={isPastDay()}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                      },
                    }}
                  />
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  background: isDark
                    ? "rgba(30, 41, 59, 0.7)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha(
                    isDark ? "#475569" : "#e2e8f0",
                    isDark ? 0.2 : 0.3
                  )}`,
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  height: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: isDark ? "#f1f5f9" : "#1e293b",
                    fontWeight: 600,
                  }}
                >
                  Transaction Stats
                </Typography>
                <Stack spacing={1.5}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                    >
                      Income Transactions:
                    </Typography>
                    <Chip
                      label={summary.manualIncomeCount}
                      size="small"
                      color="success"
                    />
                  </Box>
                  {/* <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                    >
                      Auto Income:
                    </Typography>
                    <Chip
                      label={summary.autoIncomeCount}
                      size="small"
                      color="primary"
                    />
                  </Box> */}
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                    >
                      Expense Transactions:
                    </Typography>
                    <Chip
                      label={summary.manualExpenseCount}
                      size="small"
                      color="error"
                    />
                  </Box>
                  {/* <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                    >
                      Auto Expense:
                    </Typography>
                    <Chip
                      label={summary.autoExpenseCount}
                      size="small"
                      color="error"
                    />
                  </Box> */}
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDark ? "#f1f5f9" : "#1e293b",
                        fontWeight: 600,
                      }}
                    >
                      Total Transactions:
                    </Typography>
                    <Chip
                      label={summary.totalTransactions}
                      size="medium"
                      sx={{
                        backgroundColor: isDark ? "#60a5fa" : "#2563eb",
                        color: "white",
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              {/* Day Actions Section */}
              <Paper
                sx={{
                  width: "330px",
                  p: 2,
                  borderRadius: "20px",
                  background: isDark
                    ? "rgba(30, 41, 59, 0.7)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha(
                    isDark ? "#475569" : "#e2e8f0",
                    isDark ? 0.2 : 0.3
                  )}`,
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  height: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: isDark ? "#f1f5f9" : "#1e293b",
                    fontWeight: 600,
                  }}
                >
                  Day Actions
                </Typography>
                <Stack spacing={2}>
                  {!isPastDay() && !isDayClosed(selectedRoznama) && (
                    <Button
                      variant="contained"
                      startIcon={<LockIcon />}
                      onClick={handleCloseDayClick}
                      size="medium"
                      fullWidth
                      disabled={isClosingDay}
                      sx={{
                        borderRadius: "12px",
                        background: isDark
                          ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
                          : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                        boxShadow: isDark
                          ? "0 8px 25px rgba(96, 165, 250, 0.3)"
                          : "0 8px 25px rgba(37, 99, 235, 0.3)",
                      }}
                    >
                      Close Day & Save All
                    </Button>
                  )}

                  {/* ADD THIS BUTTON FOR ADJUSTMENTS */}
                  {(isPastDay() || isDayClosed(selectedRoznama)) && selectedRoznama?.id && (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleOpenAdjustmentDialog}
                      size="medium"
                      fullWidth
                      sx={{
                        borderRadius: "12px",
                        borderColor: isDark ? "#8b5cf6" : "#8b5cf6",
                        color: isDark ? "#8b5cf6" : "#8b5cf6",
                        "&:hover": {
                          borderColor: isDark ? "#7c3aed" : "#7c3aed",
                          backgroundColor: isDark
                            ? "rgba(139, 92, 246, 0.1)"
                            : "rgba(139, 92, 246, 0.05)",
                        },
                      }}
                    >
                      Add Adjustment
                    </Button>
                  )}

                  {isDayClosed(selectedRoznama) && (
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius: "12px",
                        backgroundColor: isDark
                          ? "rgba(59, 130, 246, 0.1)"
                          : "rgba(59, 130, 246, 0.05)",
                      }}
                    >
                      This day is closed. View only mode.
                    </Alert>
                  )}
                  {isPastDay() && !isDayClosed(selectedRoznama) && (
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius: "12px",
                        backgroundColor: isDark
                          ? "rgba(59, 130, 246, 0.1)"
                          : "rgba(59, 130, 246, 0.05)",
                      }}
                    >
                      Viewing saved day. Changes disabled.
                    </Alert>
                  )}
                  {!isPastDay() &&
                    selectedRoznama?.id &&
                    !isDayClosed(selectedRoznama) && (
                      <Alert
                        severity="warning"
                        sx={{
                          borderRadius: "12px",
                          backgroundColor: isDark
                            ? "rgba(251, 191, 36, 0.1)"
                            : "rgba(251, 191, 36, 0.05)",
                        }}
                      >
                        Day already saved. Closing again will overwrite.
                      </Alert>
                    )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Quick Add Buttons */}
          {!isPastDay() && !isDayClosed(selectedRoznama) && (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                borderRadius: "20px",
                background: isDark
                  ? "rgba(30, 41, 59, 0.5)"
                  : "rgba(255, 255, 255, 0.8)",
                backdropFilter: "blur(10px)",
                border: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDark ? "#94a3b8" : "#64748b",
                  mb: 1.5,
                  fontWeight: 600,
                }}
              >
                Quick Add Common Transactions:
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                gap={2}
                sx={{ alignContent: "center", justifyContent: "center" }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    quickAddTransaction("income", "Miscellaneous Sale", 5000)
                  }
                  sx={{ borderRadius: "12px" }}
                >
                  Quick Sale ₹5,000
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    quickAddTransaction(
                      "expense",
                      "Miscellaneous Expense",
                      2000
                    )
                  }
                  sx={{ borderRadius: "12px" }}
                >
                  Quick Expense ₹2,000
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    quickAddTransaction("income", "Service Charge", 1000)
                  }
                  sx={{ borderRadius: "12px" }}
                >
                  Service ₹1,000
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    quickAddTransaction("expense", "Office Supplies", 1500)
                  }
                  sx={{ borderRadius: "12px" }}
                >
                  Supplies ₹1,500
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    quickAddTransaction("expense", "Electricity Bill", 3000)
                  }
                  sx={{ borderRadius: "12px" }}
                >
                  Electricity ₹3,000
                </Button>
              </Stack>
            </Paper>
          )}

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: "20px",
                  background: isDark
                    ? "rgba(30, 41, 59, 0.7)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha("#10b981", 0.3)}`,
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  height: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: isDark
                        ? "rgba(16, 185, 129, 0.2)"
                        : "rgba(16, 185, 129, 0.1)",
                      border: `1.5px solid ${alpha("#10b981", 0.3)}`,
                      mr: 2,
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 24, color: "#10b981" }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: isDark ? "#f1f5f9" : "#1e293b",
                      fontWeight: 700,
                    }}
                  >
                    Income Summary
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                    >
                      Cash Income:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "#10b981" }}
                    >
                      ₹{summary.cashIncome.toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                    >
                      Bank Income:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "#2563eb" }}
                    >
                      ₹{summary.bankIncome.toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDark ? "#f1f5f9" : "#1e293b",
                        fontWeight: 600,
                      }}
                    >
                      Total Income:
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 800, color: "#10b981" }}
                    >
                      ₹{summary.totalIncome.toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: "20px",
                  background: isDark
                    ? "rgba(30, 41, 59, 0.7)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha("#ef4444", 0.3)}`,
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  height: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: isDark
                        ? "rgba(239, 68, 68, 0.2)"
                        : "rgba(239, 68, 68, 0.1)",
                      border: `1.5px solid ${alpha("#ef4444", 0.3)}`,
                      mr: 2,
                    }}
                  >
                    <TrendingDownIcon sx={{ fontSize: 24, color: "#ef4444" }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: isDark ? "#f1f5f9" : "#1e293b",
                      fontWeight: 700,
                    }}
                  >
                    Expense Summary
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                    >
                      Cash Expenses:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "#ef4444" }}
                    >
                      ₹{summary.cashExpense.toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                    >
                      Bank Expenses:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "#8b5cf6" }}
                    >
                      ₹{summary.bankExpense.toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDark ? "#f1f5f9" : "#1e293b",
                        fontWeight: 600,
                      }}
                    >
                      Total Expenses:
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 800, color: "#ef4444" }}
                    >
                      ₹{summary.totalExpense.toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper
                sx={{
                  width: "670px",
                  p: 2,
                  borderRadius: "20px",
                  background: isDark
                    ? "rgba(30, 41, 59, 0.7)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha(
                    isDark ? "#475569" : "#e2e8f0",
                    isDark ? 0.2 : 0.3
                  )}`,
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: "center",
                    mb: 3,
                    color: isDark ? "#f1f5f9" : "#1e293b",
                    fontWeight: 700,
                  }}
                >
                  Balance Position
                </Typography>
                <Grid container spacing={2}>
                  {[
                    {
                      icon: <LocalAtmIcon />,
                      label: "Opening Cash",
                      value: `₹${summary.openingBalanceCash.toLocaleString(
                        "en-IN"
                      )}`,
                      color: "#10b981",
                    },
                    {
                      icon: <AccountBalanceIcon />,
                      label: "Opening Bank",
                      value: `₹${summary.openingBalanceBank.toLocaleString(
                        "en-IN"
                      )}`,
                      color: "#2563eb",
                    },
                    {
                      icon: <PaidIcon />,
                      label: "Net Profit/Loss",
                      value: `₹${summary.netProfit.toLocaleString("en-IN")}`,
                      color: summary.netProfit >= 0 ? "#10b981" : "#ef4444",
                    },
                    {
                      icon: <LocalAtmIcon />,
                      label: "Closing Cash",
                      value: `₹${summary.closingBalanceCash.toLocaleString(
                        "en-IN"
                      )}`,
                      color: "#059669",
                      change:
                        summary.closingBalanceCash - summary.openingBalanceCash,
                    },
                    {
                      icon: <AccountBalanceIcon />,
                      label: "Closing Bank",
                      value: `₹${summary.closingBalanceBank.toLocaleString(
                        "en-IN"
                      )}`,
                      color: "#1d4ed8",
                      change:
                        summary.closingBalanceBank - summary.openingBalanceBank,
                    },
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} md={2.4} key={index}>
                      <Paper
                        sx={{
                          p: 1.5,
                          textAlign: "center",
                          borderRadius: "16px",
                          background: isDark
                            ? "rgba(30, 41, 59, 0.5)"
                            : "rgba(255, 255, 255, 0.95)",
                          backdropFilter: "blur(10px)",
                          border: `1px solid ${alpha(item.color, 0.2)}`,
                          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
                          height: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            width: 43,
                            height: 43,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: alpha(item.color, 0.1),
                            margin: "0 auto 12px auto",
                            border: `1.5px solid ${alpha(item.color, 0.3)}`,
                          }}
                        >
                          {React.cloneElement(item.icon, {
                            sx: { fontSize: 24, color: item.color },
                          })}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDark ? "#94a3b8" : "#64748b",
                            mb: 0.5,
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 800,
                            color: item.color,
                            mb: 0.5,
                          }}
                        >
                          {item.value}
                        </Typography>
                        {item.change !== undefined && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? "#94a3b8" : "#64748b",
                              display: "block",
                            }}
                          >
                            {item.change >= 0 ? "↑" : "↓"}₹
                            {Math.abs(item.change).toLocaleString("en-IN")}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Tabs and Tables */}
        <Paper
          sx={{
            borderRadius: "20px",
            background: isDark
              ? "rgba(30, 41, 59, 0.8)"
              : "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(
              isDark ? "#475569" : "#e2e8f0",
              isDark ? 0.2 : 0.3
            )}`,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
            mb: 3,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              ),
              backgroundColor: isDark
                ? "rgba(30, 41, 59, 0.5)"
                : "rgba(248, 250, 252, 0.5)",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
              sx={{
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "1rem",
                  minHeight: 60,
                  color: isDark ? "#94a3b8" : "#64748b",
                  "&.Mui-selected": {
                    color: isDark ? "#60a5fa" : "#2563eb",
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: isDark ? "#60a5fa" : "#2563eb",
                  height: 3,
                  borderRadius: "3px",
                },
              }}
            >
              <Tab label={`Income (${allIncomeTransactions.length})`} />
              <Tab label={`Expenses (${allExpenseTransactions.length})`} />
            </Tabs>
          </Box>

          <Box>
            <Box
              sx={{
                p: 3,
                borderBottom: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
                backgroundColor:
                  activeTab === 0
                    ? isDark
                      ? "rgba(16, 185, 129, 0.05)"
                      : "rgba(240, 253, 244, 0.5)"
                    : isDark
                      ? "rgba(239, 68, 68, 0.05)"
                      : "rgba(254, 242, 242, 0.5)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: isDark ? "#f1f5f9" : "#1e293b",
                    fontWeight: 700,
                  }}
                >
                  {activeTab === 0
                    ? "Income Transactions"
                    : "Expense Transactions"}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: activeTab === 0 ? "#10b981" : "#ef4444",
                  }}
                >
                  Total:{" "}
                  {activeTab === 0
                    ? formatCurrency(summary.totalIncome)
                    : formatCurrency(summary.totalExpense)}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                width: "100%",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <TableContainer
                sx={{
                  maxHeight: 500,
                  width: "100%",
                  overflow: "auto",
                  "&::-webkit-scrollbar": {
                    height: 10,
                    width: 10,
                  },
                  "&::-webkit-scrollbar-track": {
                    background: isDark
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.05)",
                    borderRadius: 6,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: isDark
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.2)",
                    borderRadius: 6,
                    "&:hover": {
                      background: isDark
                        ? "rgba(255, 255, 255, 0.3)"
                        : "rgba(0, 0, 0, 0.3)",
                    },
                  },
                }}
              >
                <Table
                  stickyHeader
                  sx={{
                    width: "100%",
                    tableLayout: "auto", // Allows columns to adjust based on content
                    minWidth: "100%", // Ensure table takes full width
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {currentHeaders.map((header) => (
                        <TableCell
                          key={header.id}
                          align={header.align || "left"}
                          sx={{
                            backgroundColor: isDark
                              ? "rgba(30, 41, 59, 0.9)"
                              : "rgba(255, 255, 255, 0.95)",
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            borderBottom: `2px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.3 : 0.4
                            )}`,
                            whiteSpace: "nowrap",
                            padding: "16px 12px",
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            minWidth: header.minWidth || 120, // Minimum width for each column
                            maxWidth: header.maxWidth || "none", // Allow columns to expand
                            width: header.width || "auto", // Auto width based on content
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor:
                              header.sortable !== false ? "pointer" : "default",
                            "&:hover":
                              header.sortable !== false
                                ? {
                                  backgroundColor: isDark
                                    ? "rgba(96, 165, 250, 0.1)"
                                    : "rgba(37, 99, 235, 0.05)",
                                }
                                : {},
                            userSelect: "none",
                          }}
                          onClick={
                            header.sortable !== false
                              ? () => handleRequestSort(header.id)
                              : undefined
                          }
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent:
                                header.align === "right"
                                  ? "flex-end"
                                  : "flex-start",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            {header.sortable !== false ? (
                              <TableSortLabel
                                active={orderBy === header.id}
                                direction={
                                  orderBy === header.id ? order : "asc"
                                }
                                hideSortIcon={false}
                                sx={{
                                  "& .MuiTableSortLabel-icon": {
                                    color: isDark ? "#60a5fa" : "#2563eb",
                                  },
                                  flexGrow: 1,
                                }}
                              >
                                {header.label}
                              </TableSortLabel>
                            ) : (
                              <Box sx={{ flexGrow: 1 }}>{header.label}</Box>
                            )}
                            {header.id !== "actions" && (
                              <Box
                                sx={{
                                  width: 4,
                                  height: "100%",
                                  cursor: "col-resize",
                                  backgroundColor: "transparent",
                                  position: "absolute",
                                  right: 0,
                                  top: 0,
                                  bottom: 0,
                                  "&:hover": {
                                    backgroundColor: isDark
                                      ? "#60a5fa"
                                      : "#2563eb",
                                  },
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={currentHeaders.length}
                          align="center"
                          sx={{
                            py: 6,
                            color: isDark ? "#94a3b8" : "#64748b",
                            border: "none",
                          }}
                        >
                          <Box sx={{ py: 4 }}>
                            <ReceiptIcon
                              sx={{
                                fontSize: 64,
                                color: isDark ? "#475569" : "#e2e8f0",
                                mb: 2,
                                opacity: 0.5,
                              }}
                            />
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              No transactions found
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 3,
                                color: isDark ? "#64748b" : "#94a3b8",
                              }}
                            >
                              {activeTab === 0
                                ? "No income transactions for this date"
                                : "No expense transactions for this date"}
                            </Typography>
                            {!isPastDay() && !isDayClosed(selectedRoznama) && (
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  handleOpenDialog(
                                    activeTab === 0 ? "income" : "expense"
                                  )
                                }
                                sx={{
                                  borderRadius: "12px",
                                  background:
                                    activeTab === 0
                                      ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                                      : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                                  boxShadow:
                                    activeTab === 0
                                      ? "0 8px 25px rgba(16, 185, 129, 0.3)"
                                      : "0 8px 25px rgba(239, 68, 68, 0.3)",
                                }}
                              >
                                Add Your First Transaction
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentTransactions.map((transaction) => (
                        <TableRow
                          key={transaction.id}
                          hover
                          sx={{
                            "&:hover": {
                              backgroundColor: isDark
                                ? "rgba(255, 255, 255, 0.05)"
                                : "rgba(0, 0, 0, 0.02)",
                              transform: "translateY(-2px)",
                              boxShadow: isDark
                                ? "0 4px 12px rgba(0, 0, 0, 0.1)"
                                : "0 4px 12px rgba(0, 0, 0, 0.05)",
                              transition: "all 0.2s ease",
                            },
                            transition: "all 0.2s ease",
                            position: "relative",
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: 1,
                              background: isDark
                                ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)"
                                : "linear-gradient(90deg, transparent, rgba(0,0,0,0.03), transparent)",
                            },
                          }}
                        >
                          {/* Type */}
                          <TableCell
                            sx={{
                              padding: "12px 16px",
                              minWidth: currentHeaders[0].minWidth,
                              maxWidth: currentHeaders[0].maxWidth,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip
                                label={
                                  transaction.transactionType === "customer" ||
                                    transaction.transactionType === "supplier"
                                    ? "Auto"
                                    : transaction.transactionType === "suspense"
                                      ? "Suspense"
                                      : transaction.transactionType === "adjustment"
                                        ? "Adjustment"
                                        : "Manual"
                                }
                                size="small"
                                sx={{
                                  backgroundColor:
                                    transaction.transactionType === "suspense"
                                      ? isDark
                                        ? "rgba(168, 85, 247, 0.2)"
                                        : "rgba(168, 85, 247, 0.1)"
                                      : transaction.transactionType === "adjustment"
                                        ? isDark
                                          ? "rgba(245, 158, 11, 0.2)"
                                          : "rgba(245, 158, 11, 0.1)"
                                        : transaction.isAutoGenerated
                                          ? activeTab === 0
                                            ? isDark
                                              ? "#60a5fa"
                                              : "#2563eb"
                                            : isDark
                                              ? "#f87171"
                                              : "#dc2626"
                                          : isDark
                                            ? "#475569"
                                            : "#94a3b8",
                                  color:
                                    transaction.transactionType === "suspense"
                                      ? "#8b5cf6"
                                      : transaction.transactionType === "adjustment"
                                        ? "#f59e0b"
                                        : "white",
                                  border: transaction.transactionType === "suspense"
                                    ? `1px solid ${alpha("#8b5cf6", 0.3)}`
                                    : transaction.transactionType === "adjustment"
                                      ? `1px solid ${alpha("#f59e0b", 0.3)}`
                                      : 'none',
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  height: 24,
                                }}
                              />

                              {/* Tooltip for special entries */}
                              {transaction.transactionType === "suspense" && (
                                <Tooltip title="Suspense Adjustment Entry">
                                  <InfoIcon sx={{
                                    fontSize: 16,
                                    color: "#8b5cf6",
                                    cursor: 'pointer'
                                  }} />
                                </Tooltip>
                              )}
                              {transaction.transactionType === "adjustment" && (
                                <Tooltip title="Post-closing Adjustment Entry">
                                  <InfoIcon sx={{
                                    fontSize: 16,
                                    color: "#f59e0b",
                                    cursor: 'pointer'
                                  }} />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>

                          {/* Bill Number */}
                          <TableCell
                            sx={{
                              padding: "12px 16px",
                              minWidth: currentHeaders[1].minWidth,
                              maxWidth: currentHeaders[1].maxWidth,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontFamily: "monospace",
                                letterSpacing: 0.5,
                              }}
                            >
                              {transaction.billNumber}
                            </Typography>
                          </TableCell>

                          {/* Customer/Supplier */}
                          <TableCell
                            sx={{
                              padding: "12px 16px",
                              minWidth: currentHeaders[2].minWidth,
                              maxWidth: currentHeaders[2].maxWidth,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  fontSize: "0.875rem",
                                  bgcolor:
                                    activeTab === 0
                                      ? isDark
                                        ? "rgba(16, 185, 129, 0.2)"
                                        : "rgba(16, 185, 129, 0.1)"
                                      : isDark
                                        ? "rgba(239, 68, 68, 0.2)"
                                        : "rgba(239, 68, 68, 0.1)",
                                  color:
                                    activeTab === 0 ? "#10b981" : "#ef4444",
                                  border: `1px solid ${alpha(
                                    activeTab === 0 ? "#10b981" : "#ef4444",
                                    0.2
                                  )}`,
                                }}
                              >
                                {transaction.customerSupplier?.charAt(0)}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {transaction.customerSupplier}
                                </Typography>
                                {transaction.transactionType === "customer" &&
                                  transaction.totalPending > 0 && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#f59e0b",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      ⚡ Pending:{" "}
                                      {formatCurrency(transaction.totalPending)}
                                    </Typography>
                                  )}
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Description */}
                          <TableCell
                            sx={{
                              padding: "12px 16px",
                              minWidth: currentHeaders[3].minWidth,
                              maxWidth: currentHeaders[3].maxWidth,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {transaction.description}
                              </Typography>
                              {transaction.category && (
                                <Chip
                                  label={transaction.category}
                                  size="small"
                                  sx={{
                                    mt: 1,
                                    backgroundColor: isDark
                                      ? "rgba(255, 255, 255, 0.1)"
                                      : "rgba(0, 0, 0, 0.05)",
                                    color: isDark ? "#94a3b8" : "#64748b",
                                    fontSize: "0.625rem",
                                    height: 20,
                                    border: `1px solid ${alpha(
                                      isDark ? "#fff" : "#000",
                                      0.1
                                    )}`,
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>

                          {/* Fine (expense only) */}
                          {activeTab === 1 && (
                            <TableCell
                              align="right"
                              sx={{
                                padding: "12px 16px",
                                minWidth: currentHeaders[4].minWidth,
                                maxWidth: currentHeaders[4].maxWidth,
                              }}
                            >
                              {transaction.fine > 0 && (
                                <Chip
                                  label={formatFine(transaction.fine)}
                                  size="small"
                                  sx={{
                                    backgroundColor: isDark
                                      ? "rgba(251, 191, 36, 0.2)"
                                      : "rgba(251, 191, 36, 0.1)",
                                    color: "#f59e0b",
                                    border: `1px solid ${alpha(
                                      "#f59e0b",
                                      0.3
                                    )}`,
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </TableCell>
                          )}

                          {/* Amount */}
                          <TableCell
                            align="right"
                            sx={{
                              padding: "12px 16px",
                              minWidth:
                                currentHeaders[activeTab === 0 ? 4 : 5]
                                  .minWidth,
                              maxWidth:
                                currentHeaders[activeTab === 0 ? 4 : 5]
                                  .maxWidth,
                            }}
                          >
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                backgroundColor: alpha(
                                  activeTab === 0 ? "#10b981" : "#ef4444",
                                  0.1
                                ),
                                padding: "4px 8px",
                                borderRadius: "6px",
                                border: `1px solid ${alpha(
                                  activeTab === 0 ? "#10b981" : "#ef4444",
                                  0.2
                                )}`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 800,
                                  fontSize: "0.9rem",
                                  color:
                                    activeTab === 0 ? "#10b981" : "#ef4444",
                                }}
                              >
                                {formatCurrency(transaction.amount)}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Received/Paid */}
                          <TableCell
                            align="right"
                            sx={{
                              padding: "12px 16px",
                              minWidth:
                                currentHeaders[activeTab === 0 ? 5 : 6]
                                  .minWidth,
                              maxWidth:
                                currentHeaders[activeTab === 0 ? 5 : 6]
                                  .maxWidth,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.875rem",
                              }}
                            >
                              {activeTab === 0
                                ? formatCurrency(transaction.totalReceived)
                                : formatCurrency(transaction.totalPaid)}
                            </Typography>
                          </TableCell>

                          {/* Pending */}
                          <TableCell
                            align="right"
                            sx={{
                              padding: "12px 16px",
                              minWidth:
                                currentHeaders[activeTab === 0 ? 6 : 7]
                                  .minWidth,
                              maxWidth:
                                currentHeaders[activeTab === 0 ? 6 : 7]
                                  .maxWidth,
                            }}
                          >
                            {transaction.totalPending > 0 ? (
                              <Chip
                                label={formatCurrency(transaction.totalPending)}
                                size="small"
                                sx={{
                                  backgroundColor: isDark
                                    ? "rgba(251, 191, 36, 0.2)"
                                    : "rgba(251, 191, 36, 0.1)",
                                  color: "#f59e0b",
                                  border: `1px solid ${alpha("#f59e0b", 0.3)}`,
                                  fontWeight: 600,
                                }}
                              />
                            ) : (
                              <Chip
                                label="Full"
                                size="small"
                                sx={{
                                  backgroundColor: isDark
                                    ? "rgba(34, 197, 94, 0.2)"
                                    : "rgba(34, 197, 94, 0.1)",
                                  color: "#10b981",
                                  border: `1px solid ${alpha("#10b981", 0.3)}`,
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </TableCell>

                          {/* Bank */}
                          <TableCell
                            align="right"
                            sx={{
                              padding: "12px 16px",
                              minWidth:
                                currentHeaders[activeTab === 0 ? 7 : 8]
                                  .minWidth,
                              maxWidth:
                                currentHeaders[activeTab === 0 ? 7 : 8]
                                  .maxWidth,
                            }}
                          >
                            {transaction.bank > 0 && (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  backgroundColor: alpha("#2563eb", 0.1),
                                  padding: "4px 8px",
                                  borderRadius: "6px",
                                  border: `1px solid ${alpha("#2563eb", 0.2)}`,
                                }}
                              >
                                <AccountBalanceIcon
                                  sx={{ fontSize: 16, color: "#2563eb" }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, color: "#2563eb" }}
                                >
                                  {formatCurrency(transaction.bank)}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>

                          {/* Cash */}
                          <TableCell
                            align="right"
                            sx={{
                              padding: "12px 16px",
                              minWidth:
                                currentHeaders[activeTab === 0 ? 8 : 9]
                                  .minWidth,
                              maxWidth:
                                currentHeaders[activeTab === 0 ? 8 : 9]
                                  .maxWidth,
                            }}
                          >
                            {transaction.cash > 0 && (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  backgroundColor: alpha("#10b981", 0.1),
                                  padding: "4px 8px",
                                  borderRadius: "6px",
                                  border: `1px solid ${alpha("#10b981", 0.2)}`,
                                }}
                              >
                                <LocalAtmIcon
                                  sx={{ fontSize: 16, color: "#10b981" }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, color: "#10b981" }}
                                >
                                  {formatCurrency(transaction.cash)}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>

                          {/* Fine Paid (expense only) */}
                          {activeTab === 1 && (
                            <TableCell
                              align="right"
                              sx={{
                                padding: "12px 16px",
                                minWidth: currentHeaders[10].minWidth,
                                maxWidth: currentHeaders[10].maxWidth,
                              }}
                            >
                              {transaction.finePaid > 0 && (
                                <Chip
                                  label={formatFine(transaction.finePaid)}
                                  size="small"
                                  sx={{
                                    backgroundColor: isDark
                                      ? "rgba(34, 197, 94, 0.2)"
                                      : "rgba(34, 197, 94, 0.1)",
                                    color: "#10b981",
                                    border: `1px solid ${alpha(
                                      "#10b981",
                                      0.3
                                    )}`,
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </TableCell>
                          )}

                          {/* Payment Mode */}
                          <TableCell
                            sx={{
                              padding: "12px 16px",
                              minWidth:
                                currentHeaders[activeTab === 0 ? 9 : 11]
                                  .minWidth,
                              maxWidth:
                                currentHeaders[activeTab === 0 ? 9 : 11]
                                  .maxWidth,
                            }}
                          >
                            <Chip
                              label={
                                transaction.paymentMode === "split"
                                  ? "Cash & Bank"
                                  : transaction.paymentMode || "Cash"
                              }
                              size="small"
                              sx={{
                                backgroundColor:
                                  transaction.paymentMode === "cash"
                                    ? isDark
                                      ? "rgba(16, 185, 129, 0.2)"
                                      : "rgba(16, 185, 129, 0.1)"
                                    : transaction.paymentMode === "bank"
                                      ? isDark
                                        ? "rgba(59, 130, 246, 0.2)"
                                        : "rgba(59, 130, 246, 0.1)"
                                      : isDark
                                        ? "rgba(168, 85, 247, 0.2)"
                                        : "rgba(168, 85, 247, 0.1)",
                                color:
                                  transaction.paymentMode === "cash"
                                    ? "#10b981"
                                    : transaction.paymentMode === "bank"
                                      ? "#2563eb"
                                      : "#8b5cf6",
                                border: `1px solid ${alpha(
                                  transaction.paymentMode === "cash"
                                    ? "#10b981"
                                    : transaction.paymentMode === "bank"
                                      ? "#2563eb"
                                      : "#8b5cf6",
                                  0.3
                                )}`,
                                fontWeight: 600,
                                fontSize: "0.75rem",
                              }}
                            />
                          </TableCell>
                          {console.log('Transaction debug:', {
                            id: transaction.id,
                            isAutoGenerated: transaction.isAutoGenerated,
                            isPastDay: isPastDay(),
                            isDayClosed: isDayClosed(selectedRoznama),
                            selectedRoznama: selectedRoznama
                          })}
                          {/* Actions */}
                          <TableCell
                            sx={{
                              padding: "12px 16px",
                              minWidth:
                                currentHeaders[activeTab === 0 ? 10 : 12]
                                  .minWidth,
                              maxWidth:
                                currentHeaders[activeTab === 0 ? 10 : 12]
                                  .maxWidth,
                              position: 'sticky',
                              right: 0,
                              backgroundColor: isDark
                                ? "rgba(30, 41, 59, 0.95)"
                                : "rgba(255, 255, 255, 0.97)",
                              zIndex: 1,
                              borderLeft: `1px solid ${alpha(
                                isDark ? "#475569" : "#e2e8f0",
                                isDark ? 0.3 : 0.4
                              )}`,
                              boxShadow: isDark
                                ? "-2px 0 4px rgba(0, 0, 0, 0.2)"
                                : "-2px 0 4px rgba(0, 0, 0, 0.1)",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "center",
                              }}
                            >
                              {/* VIEW BUTTON - Show for ALL transaction types */}
                              <Tooltip title="View Transaction Details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewTransactionDetails(transaction)}
                                  sx={{
                                    backgroundColor: isDark
                                      ? "rgba(59, 130, 246, 0.1)"
                                      : "rgba(59, 130, 246, 0.05)",
                                    color: "#2563eb",
                                    "&:hover": {
                                      backgroundColor: isDark
                                        ? "rgba(59, 130, 246, 0.2)"
                                        : "rgba(59, 130, 246, 0.1)",
                                    },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              {/* EDIT and DELETE buttons - Conditional based on rules */}
                              {!transaction.isAutoGenerated && (
                                <>
                                  {/* Allow editing for: 
          1. Manual transactions on current day
          2. Suspense entries (anytime)
          3. Adjustment records (anytime)
        */}
                                  {(transaction.transactionType === "manual" && !isPastDay() && !isDayClosed(selectedRoznama)) ||
                                    transaction.transactionType === "suspense" ||
                                    (transaction.transactionType === "adjustment" && transaction.isAdjustmentRecord) ? (
                                    <Tooltip title={
                                      transaction.transactionType === "suspense"
                                        ? "Edit Suspense Entry"
                                        : "Edit Transaction"
                                    }>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          if (transaction.transactionType === "suspense") {
                                            handleEditSuspenseEntry(transaction);
                                          } else {
                                            handleEditTransaction(transaction);
                                          }
                                        }}
                                        sx={{
                                          backgroundColor: isDark
                                            ? "rgba(59, 130, 246, 0.1)"
                                            : "rgba(59, 130, 246, 0.05)",
                                          color: "#2563eb",
                                          "&:hover": {
                                            backgroundColor: isDark
                                              ? "rgba(59, 130, 246, 0.2)"
                                              : "rgba(59, 130, 246, 0.1)",
                                          },
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : null}

                                  {/* Allow deleting for same conditions */}
                                  {(transaction.transactionType === "manual" && !isPastDay() && !isDayClosed(selectedRoznama)) ||
                                    transaction.transactionType === "suspense" ||
                                    (transaction.transactionType === "adjustment" && transaction.isAdjustmentRecord) ? (
                                    <Tooltip title={
                                      transaction.transactionType === "suspense"
                                        ? "Delete Suspense Entry"
                                        : "Delete Transaction"
                                    }>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleDeleteTransaction(transaction)
                                        }
                                        sx={{
                                          backgroundColor: isDark
                                            ? "rgba(239, 68, 68, 0.1)"
                                            : "rgba(239, 68, 68, 0.05)",
                                          color: "#ef4444",
                                          "&:hover": {
                                            backgroundColor: isDark
                                              ? "rgba(239, 68, 68, 0.2)"
                                              : "rgba(239, 68, 68, 0.1)",
                                          },
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : null}
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={
                activeTab === 0
                  ? allIncomeTransactions.length
                  : allExpenseTransactions.length
              }
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                backgroundColor: isDark
                  ? "rgba(30, 41, 59, 0.5)"
                  : "rgba(248, 250, 252, 0.5)",
                borderTop: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  color: isDark ? "#94a3b8" : "#64748b",
                },
              }}
            />
          </Box>
        </Paper>

        {/* Transaction Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "20px",
              background: isDark
                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
              backdropFilter: "blur(30px)",
              border: `1px solid ${alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <DialogTitle
            sx={{
              color: isDark ? "#f1f5f9" : "#1e293b",
              fontWeight: 700,
              borderBottom: `1px solid ${alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
            }}
          >
            {editingTransaction
              ? "Edit Transaction"
              : newTransaction.type === "income"
                ? "New Income Entry"
                : "New Expense Entry"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bill Number"
                  name="billNumber"
                  value={newTransaction.billNumber}
                  onChange={handleInputChange}
                  required
                  disabled={editingTransaction}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={newTransaction.category}
                    onChange={handleInputChange}
                    label="Category"
                    sx={{
                      borderRadius: "12px",
                    }}
                  >
                    {newTransaction.type === "income"
                      ? incomeCategories.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))
                      : expenseCategories.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={newTransaction.description}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Total Amount"
                  name="amount"
                  type="number"
                  value={newTransaction.amount}
                  onChange={handleInputChange}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                    },
                  }}
                />
              </Grid>

              {/* Payment split section */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ color: isDark ? "#cbd5e1" : "#475569" }}
                >
                  Payment Split (Cash/Bank)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Cash Amount"
                      type="number"
                      value={
                        newTransaction.paymentMode === "cash" ||
                          newTransaction.paymentMode === "split"
                          ? newTransaction.cashPortion || ""
                          : ""
                      }
                      onChange={(e) => {
                        const cashAmount = parseFloat(e.target.value) || 0;
                        setNewTransaction((prev) => ({
                          ...prev,
                          cashPortion: cashAmount,
                          amount: cashAmount + (prev.bankPortion || 0),
                          paymentMode: prev.bankPortion > 0 ? "split" : "cash",
                        }));
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Bank Amount"
                      type="number"
                      value={
                        newTransaction.paymentMode === "bank" ||
                          newTransaction.paymentMode === "split"
                          ? newTransaction.bankPortion || ""
                          : ""
                      }
                      onChange={(e) => {
                        const bankAmount = parseFloat(e.target.value) || 0;
                        setNewTransaction((prev) => ({
                          ...prev,
                          bankPortion: bankAmount,
                          amount: (prev.cashPortion || 0) + bankAmount,
                          paymentMode: prev.cashPortion > 0 ? "split" : "bank",
                        }));
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Payment Mode</InputLabel>
                  <Select
                    name="paymentMode"
                    value={newTransaction.paymentMode}
                    onChange={handleInputChange}
                    label="Payment Mode"
                    sx={{
                      borderRadius: "12px",
                    }}
                  >
                    <MenuItem value="cash">Cash Only</MenuItem>
                    <MenuItem value="bank">Bank Only</MenuItem>
                    <MenuItem value="split">Cash & Bank Split</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* For expense transactions, add fine fields */}
              {newTransaction.type === "expense" && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Fine (grams)"
                      name="fine"
                      type="number"
                      value={newTransaction.fine || ""}
                      onChange={handleInputChange}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Fine Paid (grams)"
                      name="finePaid"
                      type="number"
                      value={newTransaction.finePaid || ""}
                      onChange={handleInputChange}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                        },
                      }}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remark"
                  name="remark"
                  value={newTransaction.remark}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            sx={{
              p: 3,
              borderTop: `1px solid ${alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
            }}
          >
            <Button onClick={handleCloseDialog} sx={{ borderRadius: "12px" }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitTransaction}
              variant="contained"
              disabled={
                !newTransaction.billNumber ||
                !newTransaction.description ||
                !newTransaction.amount
              }
              sx={{
                borderRadius: "12px",
                fontWeight: 600,
                px: 4,
                background: isDark
                  ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
                  : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
              }}
            >
              {editingTransaction ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Close Day Dialog - Actual Balances */}
        <Dialog
          open={closeDayDialogOpen}
          onClose={() => setCloseDayDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "20px",
              background: isDark
                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
              backdropFilter: "blur(30px)",
              border: `1px solid ${alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <DialogTitle
            sx={{
              color: isDark ? "#f1f5f9" : "#1e293b",
              fontWeight: 700,
              borderBottom: `1px solid ${alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon sx={{ color: isDark ? "#60a5fa" : "#2563eb" }} />
              Close Day - Verify Actual Balances
            </Box>
          </DialogTitle>

          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3, color: isDark ? "#94a3b8" : "#64748b" }}>
                Please enter the actual cash and bank balances as per physical verification.
              </Typography>

              {/* Calculated Balances Summary */}
              <Paper sx={{
                p: 2,
                mb: 3,
                borderRadius: "12px",
                background: isDark
                  ? "rgba(59, 130, 246, 0.1)"
                  : "rgba(59, 130, 246, 0.05)",
                border: `1px solid ${alpha("#2563eb", 0.2)}`
              }}>
                <Typography variant="subtitle2" sx={{
                  fontWeight: 600,
                  color: isDark ? "#60a5fa" : "#2563eb",
                  mb: 1
                }}>
                  Software Calculated Balances:
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: isDark ? "#cbd5e1" : "#475569" }}>
                      Cash: ₹{summary.closingBalanceCash.toLocaleString('en-IN')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: isDark ? "#cbd5e1" : "#475569" }}>
                      Bank: ₹{summary.closingBalanceBank.toLocaleString('en-IN')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{
                      fontWeight: 600,
                      color: isDark ? "#f1f5f9" : "#1e293b",
                      mt: 1
                    }}>
                      Total: ₹{summary.totalClosingBalance.toLocaleString('en-IN')}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Actual Balance Inputs */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Actual Cash Balance"
                    type="number"
                    value={actualBalances.cash}
                    onChange={(e) => handleActualBalanceChange("cash", e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalAtmIcon sx={{
                            fontSize: 20,
                            color: differences.cash === 0 ? "inherit" :
                              differences.cash > 0 ? "#10b981" : "#ef4444"
                          }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Actual Bank Balance"
                    type="number"
                    value={actualBalances.bank}
                    onChange={(e) => handleActualBalanceChange("bank", e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountBalanceIcon sx={{
                            fontSize: 20,
                            color: differences.bank === 0 ? "inherit" :
                              differences.bank > 0 ? "#10b981" : "#ef4444"
                          }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {/* Differences Display */}
              {(differences.cash !== 0 || differences.bank !== 0) && (
                <Paper sx={{
                  p: 2,
                  mt: 3,
                  borderRadius: "12px",
                  background: differences.total === 0
                    ? isDark ? "rgba(251, 191, 36, 0.1)" : "rgba(251, 191, 36, 0.05)"
                    : differences.total > 0
                      ? isDark ? "rgba(16, 185, 129, 0.1)" : "rgba(16, 185, 129, 0.05)"
                      : isDark ? "rgba(239, 68, 68, 0.1)" : "rgba(239, 68, 68, 0.05)",
                  border: `1px solid ${alpha(
                    differences.total === 0 ? "#f59e0b" :
                      differences.total > 0 ? "#10b981" : "#ef4444",
                    0.3
                  )}`
                }}>
                  <Typography variant="subtitle2" sx={{
                    fontWeight: 600,
                    color: differences.total === 0 ? "#f59e0b" :
                      differences.total > 0 ? "#10b981" : "#ef4444",
                    mb: 1
                  }}>
                    Differences Detected:
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocalAtmIcon sx={{
                          fontSize: 16,
                          color: differences.cash === 0 ? "inherit" :
                            differences.cash > 0 ? "#10b981" : "#ef4444"
                        }} />
                        <Typography variant="body2">
                          Cash:
                          <span style={{
                            fontWeight: 600,
                            color: differences.cash === 0 ? "inherit" :
                              differences.cash > 0 ? "#10b981" : "#ef4444",
                            marginLeft: 4
                          }}>
                            {differences.cash > 0 ? '+' : ''}₹{Math.abs(differences.cash).toLocaleString('en-IN')}
                          </span>
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccountBalanceIcon sx={{
                          fontSize: 16,
                          color: differences.bank === 0 ? "inherit" :
                            differences.bank > 0 ? "#10b981" : "#ef4444"
                        }} />
                        <Typography variant="body2">
                          Bank:
                          <span style={{
                            fontWeight: 600,
                            color: differences.bank === 0 ? "inherit" :
                              differences.bank > 0 ? "#10b981" : "#ef4444",
                            marginLeft: 4
                          }}>
                            {differences.bank > 0 ? '+' : ''}₹{Math.abs(differences.bank).toLocaleString('en-IN')}
                          </span>
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Net Difference:
                        </Typography>
                        <Typography variant="body1" sx={{
                          fontWeight: 700,
                          color: differences.total === 0 ? "#f59e0b" :
                            differences.total > 0 ? "#10b981" : "#ef4444"
                        }}>
                          {differences.total > 0 ? '+' : ''}₹{Math.abs(differences.total).toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{
                        color: isDark ? "#94a3b8" : "#64748b",
                        display: 'block',
                        mt: 1
                      }}>
                        {differences.total === 0
                          ? "No suspense entry needed"
                          : differences.total > 0
                            ? "Suspense Income entry will be created"
                            : "Suspense Expense entry will be created"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              p: 3,
              borderTop: `1px solid ${alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
            }}
          >
            <Button
              onClick={() => setCloseDayDialogOpen(false)}
              sx={{ borderRadius: "12px" }}
              disabled={isClosingDay}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmCloseDay}
              variant="contained"
              disabled={isClosingDay}
              sx={{
                borderRadius: "12px",
                fontWeight: 600,
                px: 4,
                background: isDark
                  ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                  : "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              }}
            >
              {isClosingDay ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Confirm & Close Day"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Adjustment Dialog for Past Dates */}
        {/* Adjustment Dialog for Past Dates */}
        <Dialog
          open={adjustmentDialogOpen}
          onClose={() => {
            setAdjustmentDialogOpen(false);
            // setAdjustmentMode(false);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "20px",
              background: isDark
                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
              backdropFilter: "blur(30px)",
              border: `1px solid ${alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <DialogTitle
            sx={{
              color: isDark ? "#f1f5f9" : "#1e293b",
              fontWeight: 700,
              borderBottom: `1px solid ${alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon sx={{ color: isDark ? "#8b5cf6" : "#8b5cf6" }} />
              Add Adjustment for {format(parseISO(selectedDate), 'dd/MM/yyyy')}
            </Box>
          </DialogTitle>

          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {/* Find existing suspense entry */}
              {(() => {
                const suspenseEntry = [
                  ...manualTransactions.income,
                  ...manualTransactions.expense
                ].find(t => t.transactionType === "suspense");

                return suspenseEntry ? (
                  <Alert
                    severity="info"
                    sx={{
                      mb: 3,
                      borderRadius: "12px",
                      backgroundColor: isDark
                        ? "rgba(139, 92, 246, 0.1)"
                        : "rgba(139, 92, 246, 0.05)",
                      border: `1px solid ${alpha("#8b5cf6", 0.2)}`
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <InfoIcon sx={{ fontSize: 20, mt: 0.25 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Existing Suspense Entry Found
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          {suspenseEntry.type === "income" ? "📈 Suspense Income" : "📉 Suspense Expense"}:
                          <span style={{ fontWeight: 700, marginLeft: 4 }}>
                            ₹{suspenseEntry.amount.toLocaleString('en-IN')}
                          </span>
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          {suspenseEntry.cash > 0 && `💰 Cash: ₹${suspenseEntry.cash.toLocaleString('en-IN')} `}
                          {suspenseEntry.bank > 0 && `🏦 Bank: ₹${suspenseEntry.bank.toLocaleString('en-IN')}`}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                          This adjustment will modify the existing suspense entry while keeping closing balances unchanged.
                        </Typography>
                      </Box>
                    </Box>
                  </Alert>
                ) : (
                  <Alert
                    severity="warning"
                    sx={{
                      mb: 3,
                      borderRadius: "12px",
                      backgroundColor: isDark
                        ? "rgba(245, 158, 11, 0.1)"
                        : "rgba(245, 158, 11, 0.05)",
                      border: `1px solid ${alpha("#f59e0b", 0.2)}`
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <InfoIcon sx={{ fontSize: 20, mt: 0.25 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          No Suspense Entry Found
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          A new suspense entry will be created to balance this adjustment.
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                          Closing balances will remain as verified:
                          Cash ₹{summary.closingBalanceCash.toLocaleString('en-IN')},
                          Bank ₹{summary.closingBalanceBank.toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    </Box>
                  </Alert>
                );
              })()}

              {/* Key Information */}
              <Paper sx={{
                p: 2,
                mb: 3,
                borderRadius: "12px",
                background: isDark
                  ? "rgba(59, 130, 246, 0.05)"
                  : "rgba(59, 130, 246, 0.03)",
                border: `1px solid ${alpha("#2563eb", 0.1)}`
              }}>
                <Typography variant="body2" sx={{
                  color: isDark ? "#94a3b8" : "#64748b",
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  <InfoIcon sx={{ fontSize: 16 }} />
                  Important Note:
                </Typography>
                <Typography variant="caption" sx={{
                  color: isDark ? "#cbd5e1" : "#475569",
                  display: 'block'
                }}>
                  • Adjustments modify the suspense entry, not the closing balances
                  <br />
                  • Actual cash/bank balances remain as per physical verification
                  <br />
                  • Each adjustment is tracked in the suspense entry remarks
                </Typography>
              </Paper>

              {/* Adjustment Form */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Adjustment Type</InputLabel>
                    <Select
                      name="type"
                      value={newTransaction.type}
                      onChange={handleInputChange}
                      label="Adjustment Type"
                      sx={{
                        borderRadius: "12px",
                        "& .MuiSelect-select": {
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }
                      }}
                    >
                      <MenuItem value="income">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUpIcon sx={{ fontSize: 18, color: "#10b981" }} />
                          <span>Income Adjustment (Received Money)</span>
                        </Box>
                      </MenuItem>
                      <MenuItem value="expense">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingDownIcon sx={{ fontSize: 18, color: "#ef4444" }} />
                          <span>Expense Adjustment (Paid Money)</span>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={newTransaction.description}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                    required
                    placeholder="e.g., Late payment from customer, Additional payment to supplier, Bank charge missed"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ReceiptIcon sx={{ fontSize: 20, color: isDark ? "#94a3b8" : "#64748b" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Amount"
                    name="amount"
                    type="number"
                    value={newTransaction.amount}
                    onChange={handleInputChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PaidIcon sx={{ fontSize: 20, color: isDark ? "#94a3b8" : "#64748b" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{
                      color: isDark ? "#cbd5e1" : "#475569",
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <AccountBalanceWallet sx={{ fontSize: 18 }} />
                    Payment Split (Cash/Bank)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Cash Amount"
                        type="number"
                        value={
                          newTransaction.paymentMode === "cash" ||
                            newTransaction.paymentMode === "split"
                            ? newTransaction.cashPortion || ""
                            : ""
                        }
                        onChange={(e) => {
                          const cashAmount = parseFloat(e.target.value) || 0;
                          setNewTransaction((prev) => ({
                            ...prev,
                            cashPortion: cashAmount,
                            amount: cashAmount + (prev.bankPortion || 0),
                            paymentMode: prev.bankPortion > 0 ? "split" : "cash",
                          }));
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocalAtmIcon sx={{ fontSize: 20, color: "#10b981" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Bank Amount"
                        type="number"
                        value={
                          newTransaction.paymentMode === "bank" ||
                            newTransaction.paymentMode === "split"
                            ? newTransaction.bankPortion || ""
                            : ""
                        }
                        onChange={(e) => {
                          const bankAmount = parseFloat(e.target.value) || 0;
                          setNewTransaction((prev) => ({
                            ...prev,
                            bankPortion: bankAmount,
                            amount: (prev.cashPortion || 0) + bankAmount,
                            paymentMode: prev.cashPortion > 0 ? "split" : "bank",
                          }));
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccountBalanceIcon sx={{ fontSize: 20, color: "#2563eb" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Mode</InputLabel>
                    <Select
                      name="paymentMode"
                      value={newTransaction.paymentMode}
                      onChange={handleInputChange}
                      label="Payment Mode"
                      sx={{
                        borderRadius: "12px",
                        "& .MuiSelect-select": {
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }
                      }}
                    >
                      <MenuItem value="cash">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalAtmIcon sx={{ fontSize: 18, color: "#10b981" }} />
                          <span>Cash Only</span>
                        </Box>
                      </MenuItem>
                      <MenuItem value="bank">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccountBalanceIcon sx={{ fontSize: 18, color: "#2563eb" }} />
                          <span>Bank Only</span>
                        </Box>
                      </MenuItem>
                      <MenuItem value="split">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PaidIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
                          <span>Cash & Bank Split</span>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {newTransaction.type === "expense" && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Fine (grams)"
                        name="fine"
                        type="number"
                        value={newTransaction.fine || ""}
                        onChange={handleInputChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <DiamondIcon sx={{ fontSize: 20, color: "#f59e0b" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Fine Paid (grams)"
                        name="finePaid"
                        type="number"
                        value={newTransaction.finePaid || ""}
                        onChange={handleInputChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <DiamondIcon sx={{ fontSize: 20, color: "#10b981" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remark / Reason"
                    name="remark"
                    value={newTransaction.remark}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                    placeholder="Detailed reason for this adjustment"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EditIcon sx={{ fontSize: 20, color: isDark ? "#94a3b8" : "#64748b" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                      },
                    }}
                  />
                </Grid>

                {/* Show impact preview */}
                {newTransaction.amount && (
                  <Grid item xs={12}>
                    <Paper sx={{
                      p: 2,
                      borderRadius: "12px",
                      background: isDark
                        ? "rgba(139, 92, 246, 0.1)"
                        : "rgba(139, 92, 246, 0.05)",
                      border: `1px solid ${alpha("#8b5cf6", 0.2)}`
                    }}>
                      <Typography variant="subtitle2" sx={{
                        fontWeight: 600,
                        color: isDark ? "#8b5cf6" : "#8b5cf6",
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}>
                        <InfoIcon sx={{ fontSize: 18 }} />
                        Impact on Suspense Entry:
                      </Typography>

                      {/* Find existing suspense entry for preview */}
                      {(() => {
                        const suspenseEntry = [
                          ...manualTransactions.income,
                          ...manualTransactions.expense
                        ].find(t => t.transactionType === "suspense");

                        if (!suspenseEntry) {
                          return (
                            <Box>
                              <Typography variant="body2" sx={{ color: isDark ? "#cbd5e1" : "#475569", mb: 1 }}>
                                New suspense entry will be created as:
                              </Typography>
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <Typography variant="body2" sx={{ color: isDark ? "#cbd5e1" : "#475569" }}>
                                    Type:
                                  </Typography>
                                  <Typography variant="body1" sx={{
                                    fontWeight: 700,
                                    color: newTransaction.type === "income" ? "#ef4444" : "#10b981"
                                  }}>
                                    {newTransaction.type === "income" ? "Suspense Expense" : "Suspense Income"}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="body2" sx={{ color: isDark ? "#cbd5e1" : "#475569" }}>
                                    Amount:
                                  </Typography>
                                  <Typography variant="body1" sx={{
                                    fontWeight: 700,
                                    color: newTransaction.type === "income" ? "#ef4444" : "#10b981"
                                  }}>
                                    ₹{parseFloat(newTransaction.amount).toLocaleString('en-IN')}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          );
                        }

                        // Calculate new suspense values
                        let newSuspenseAmount = suspenseEntry.amount;
                        let newSuspenseType = suspenseEntry.type;
                        let newSuspenseCash = suspenseEntry.cash;
                        let newSuspenseBank = suspenseEntry.bank;

                        const adjustmentAmount = parseFloat(newTransaction.amount) || 0;
                        const adjustmentCash = newTransaction.paymentMode === "cash"
                          ? adjustmentAmount
                          : parseFloat(newTransaction.cashPortion) || 0;
                        const adjustmentBank = newTransaction.paymentMode === "bank"
                          ? adjustmentAmount
                          : parseFloat(newTransaction.bankPortion) || 0;

                        if (newTransaction.type === "income") {
                          if (suspenseEntry.type === "income") {
                            newSuspenseAmount -= adjustmentAmount;
                            newSuspenseCash -= adjustmentCash;
                            newSuspenseBank -= adjustmentBank;

                            if (newSuspenseAmount < 0) {
                              newSuspenseType = "expense";
                              newSuspenseAmount = Math.abs(newSuspenseAmount);
                              newSuspenseCash = Math.abs(newSuspenseCash);
                              newSuspenseBank = Math.abs(newSuspenseBank);
                            }
                          } else {
                            newSuspenseAmount += adjustmentAmount;
                            newSuspenseCash += adjustmentCash;
                            newSuspenseBank += adjustmentBank;
                          }
                        } else {
                          if (suspenseEntry.type === "expense") {
                            newSuspenseAmount -= adjustmentAmount;
                            newSuspenseCash -= adjustmentCash;
                            newSuspenseBank -= adjustmentBank;

                            if (newSuspenseAmount < 0) {
                              newSuspenseType = "income";
                              newSuspenseAmount = Math.abs(newSuspenseAmount);
                              newSuspenseCash = Math.abs(newSuspenseCash);
                              newSuspenseBank = Math.abs(newSuspenseBank);
                            }
                          } else {
                            newSuspenseAmount += adjustmentAmount;
                            newSuspenseCash += adjustmentCash;
                            newSuspenseBank += adjustmentBank;
                          }
                        }

                        return (
                          <Box>
                            <Typography variant="body2" sx={{ color: isDark ? "#cbd5e1" : "#475569", mb: 1 }}>
                              Suspense entry will be updated:
                            </Typography>
                            <Grid container spacing={1}>
                              <Grid item xs={4}>
                                <Typography variant="body2" sx={{ color: isDark ? "#cbd5e1" : "#475569" }}>
                                  From:
                                </Typography>
                                <Chip
                                  label={suspenseEntry.type === "income" ? "Income" : "Expense"}
                                  size="small"
                                  sx={{
                                    backgroundColor: suspenseEntry.type === "income"
                                      ? alpha("#10b981", 0.2)
                                      : alpha("#ef4444", 0.2),
                                    color: suspenseEntry.type === "income" ? "#10b981" : "#ef4444",
                                    fontWeight: 600,
                                    mt: 0.5
                                  }}
                                />
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                  ₹{suspenseEntry.amount.toLocaleString('en-IN')}
                                </Typography>
                              </Grid>
                              <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ArrowForwardIcon sx={{
                                  fontSize: 20,
                                  color: isDark ? "#94a3b8" : "#64748b"
                                }} />
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" sx={{ color: isDark ? "#cbd5e1" : "#475569" }}>
                                  To:
                                </Typography>
                                <Chip
                                  label={newSuspenseType === "income" ? "Income" : "Expense"}
                                  size="small"
                                  sx={{
                                    backgroundColor: newSuspenseType === "income"
                                      ? alpha("#10b981", 0.2)
                                      : alpha("#ef4444", 0.2),
                                    color: newSuspenseType === "income" ? "#10b981" : "#ef4444",
                                    fontWeight: 600,
                                    mt: 0.5
                                  }}
                                />
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                  ₹{newSuspenseAmount.toLocaleString('en-IN')}
                                </Typography>
                              </Grid>
                            </Grid>

                            {/* Cash/Bank breakdown */}
                            {(newSuspenseCash > 0 || newSuspenseBank > 0) && (
                              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${alpha("#8b5cf6", 0.1)}` }}>
                                <Typography variant="caption" sx={{
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  display: 'block',
                                  mb: 0.5
                                }}>
                                  Breakdown:
                                </Typography>
                                <Grid container spacing={1}>
                                  {newSuspenseCash > 0 && (
                                    <Grid item xs={6}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <LocalAtmIcon sx={{ fontSize: 14, color: "#10b981" }} />
                                        <Typography variant="caption" sx={{ color: isDark ? "#cbd5e1" : "#475569" }}>
                                          Cash: ₹{newSuspenseCash.toLocaleString('en-IN')}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {newSuspenseBank > 0 && (
                                    <Grid item xs={6}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AccountBalanceIcon sx={{ fontSize: 14, color: "#2563eb" }} />
                                        <Typography variant="caption" sx={{ color: isDark ? "#cbd5e1" : "#475569" }}>
                                          Bank: ₹{newSuspenseBank.toLocaleString('en-IN')}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            )}

                            {/* Closing balances reminder */}
                            <Box sx={{
                              mt: 1.5,
                              p: 1,
                              borderRadius: "8px",
                              backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)"
                            }}>
                              <Typography variant="caption" sx={{
                                color: isDark ? "#94a3b8" : "#64748b",
                                fontStyle: 'italic',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}>
                                <LockIcon sx={{ fontSize: 12 }} />
                                Closing balances remain unchanged
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })()}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              p: 3,
              borderTop: `1px solid ${alpha(
                isDark ? "#475569" : "#e2e8f0",
                isDark ? 0.2 : 0.3
              )}`,
            }}
          >
            <Button
              onClick={() => {
                setAdjustmentDialogOpen(false);
                // setAdjustmentMode(false);
              }}
              sx={{
                borderRadius: "12px",
                color: isDark ? "#94a3b8" : "#64748b"
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAdjustment}
              variant="contained"
              disabled={
                !newTransaction.description ||
                !newTransaction.amount ||
                parseFloat(newTransaction.amount) <= 0
              }
              sx={{
                borderRadius: "12px",
                fontWeight: 600,
                px: 4,
                background: isDark
                  ? "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)"
                  : "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
                boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)",
                "&:hover": {
                  background: isDark
                    ? "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)"
                    : "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
                },
                "&:disabled": {
                  background: isDark ? "#475569" : "#e2e8f0",
                  color: isDark ? "#94a3b8" : "#64748b",
                }
              }}
            >
              Save Adjustment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{
              width: "100%",
              borderRadius: "12px",
              backdropFilter: "blur(10px)",
              backgroundColor: isDark
                ? "rgba(30, 41, 59, 0.9)"
                : "rgba(255, 255, 255, 0.95)",
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>

    )}
    {/* </Box> */}
  {/* ); */}
      {/* Daily Report Modal */}
      <Dialog
        open={showDailyReport}
        onClose={() => setShowDailyReport(false)}
        maxWidth="xl"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            background: isDark
              ? "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
              : "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)",
            backdropFilter: "blur(30px)",
            border: `1px solid ${alpha(
              isDark ? "#475569" : "#e2e8f0",
              isDark ? 0.2 : 0.3
            )}`,
            boxShadow: "0 25px 80px rgba(0, 0, 0, 0.2)",
            minHeight: "85vh",
            maxHeight: "90vh",
          },
        }}
      >
        {dailyReportData && (
          <>
            <Box
              sx={{
                p: 4,
                borderBottom: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
                background: isDark
                  ? "linear-gradient(90deg, rgba(96, 165, 250, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)"
                  : "linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.04) 100%)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: isDark ? "#f1f5f9" : "#1e293b",
                      mb: 1,
                    }}
                  >
                    Daily Financial Report
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: isDark ? "#94a3b8" : "#64748b",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CalendarToday sx={{ fontSize: 20 }} />
                    {format(
                      parseISO(dailyReportData.date),
                      "EEEE, dd MMMM yyyy"
                    )}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setShowDailyReport(false)}
                  sx={{
                    backgroundColor: isDark
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.05)",
                    color: isDark ? "#94a3b8" : "#64748b",
                    "&:hover": {
                      backgroundColor: isDark
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Opening Balance Cards */}
              <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                <Paper
                  sx={{
                    flex: 1,
                    p: 3,
                    borderRadius: "16px",
                    background: isDark
                      ? "linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.15) 100%)"
                      : "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.08) 100%)",
                    border: `1px solid ${alpha("#10b981", 0.3)}`,
                    boxShadow: "0 4px 20px rgba(16, 185, 129, 0.15)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(255, 255, 255, 0.2)",
                        mr: 2,
                      }}
                    >
                      <LocalAtmIcon sx={{ fontSize: 28, color: "#10b981" }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                      >
                        Opening Cash Balance
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: "#10b981" }}
                      >
                        ₹
                        {dailyReportData.openingBalanceCash.toLocaleString(
                          "en-IN"
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                <Paper
                  sx={{
                    flex: 1,
                    p: 3,
                    borderRadius: "16px",
                    background: isDark
                      ? "linear-gradient(135deg, rgba(37, 99, 235, 0.2) 0%, rgba(29, 78, 216, 0.15) 100%)"
                      : "linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(29, 78, 216, 0.08) 100%)",
                    border: `1px solid ${alpha("#2563eb", 0.3)}`,
                    boxShadow: "0 4px 20px rgba(37, 99, 235, 0.15)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(255, 255, 255, 0.2)",
                        mr: 2,
                      }}
                    >
                      <AccountBalanceIcon
                        sx={{ fontSize: 28, color: "#2563eb" }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                      >
                        Opening Bank Balance
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: "#2563eb" }}
                      >
                        ₹
                        {dailyReportData.openingBalanceBank.toLocaleString(
                          "en-IN"
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
              }}
            >
              {/* Left Side - Income Section */}
              <Box
                sx={{
                  flex: 1,
                  p: 4,
                  borderRight: {
                    lg: `1px solid ${alpha(
                      isDark ? "#475569" : "#e2e8f0",
                      isDark ? 0.2 : 0.3
                    )}`,
                  },
                  borderBottom: {
                    xs: `1px solid ${alpha(
                      isDark ? "#475569" : "#e2e8f0",
                      isDark ? 0.2 : 0.3
                    )}`,
                    lg: "none",
                  },
                  overflow: "auto",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: isDark
                        ? "linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.15) 100%)"
                        : "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.08) 100%)",
                      border: `2px solid ${alpha("#10b981", 0.3)}`,
                      mr: 3,
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 28, color: "#10b981" }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: isDark ? "#f1f5f9" : "#1e293b",
                      }}
                    >
                      Income Summary
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                    >
                      Detailed breakdown of all income transactions
                    </Typography>
                  </Box>
                </Box>

                {/* Cash Income Transactions */}
                {dailyReportData.income.cashTransactions.length > 0 && (
                  <Paper
                    sx={{ mb: 3, borderRadius: "16px", overflow: "hidden" }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        background: isDark
                          ? "linear-gradient(90deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)"
                          : "linear-gradient(90deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.06) 100%)",
                        borderBottom: `1px solid ${alpha("#10b981", 0.2)}`,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LocalAtmIcon sx={{ color: "#10b981" }} />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        >
                          Cash Income Transactions
                        </Typography>
                        <Chip
                          label={`${dailyReportData.income.cashTransactions.length} transactions`}
                          size="small"
                          sx={{
                            backgroundColor: alpha("#10b981", 0.2),
                            color: "#10b981",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Bill No.
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Customer
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Description
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Amount
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dailyReportData.income.cashTransactions.map(
                            (transaction, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: "monospace" }}
                                  >
                                    {transaction.billNumber}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {transaction.customerSupplier}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                    }}
                                  >
                                    {transaction.description}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#10b981" }}
                                  >
                                    ₹
                                    {transaction.amount.toLocaleString("en-IN")}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {/* Bank Income Transactions */}
                {dailyReportData.income.bankTransactions.length > 0 && (
                  <Paper
                    sx={{ mb: 3, borderRadius: "16px", overflow: "hidden" }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        background: isDark
                          ? "linear-gradient(90deg, rgba(37, 99, 235, 0.15) 0%, rgba(29, 78, 216, 0.1) 100%)"
                          : "linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.06) 100%)",
                        borderBottom: `1px solid ${alpha("#2563eb", 0.2)}`,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <AccountBalanceIcon sx={{ color: "#2563eb" }} />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        >
                          Bank Income Transactions
                        </Typography>
                        <Chip
                          label={`${dailyReportData.income.bankTransactions.length} transactions`}
                          size="small"
                          sx={{
                            backgroundColor: alpha("#2563eb", 0.2),
                            color: "#2563eb",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Bill No.
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Customer
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Description
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Amount
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dailyReportData.income.bankTransactions.map(
                            (transaction, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: "monospace" }}
                                  >
                                    {transaction.billNumber}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {transaction.customerSupplier}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                    }}
                                  >
                                    {transaction.description}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#2563eb" }}
                                  >
                                    ₹
                                    {transaction.amount.toLocaleString("en-IN")}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {/* Split Income Transactions */}
                {dailyReportData.income.splitTransactions.length > 0 && (
                  <Paper
                    sx={{ mb: 3, borderRadius: "16px", overflow: "hidden" }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        background: isDark
                          ? "linear-gradient(90deg, rgba(168, 85, 247, 0.15) 0%, rgba(147, 51, 234, 0.1) 100%)"
                          : "linear-gradient(90deg, rgba(168, 85, 247, 0.08) 0%, rgba(147, 51, 234, 0.06) 100%)",
                        borderBottom: `1px solid ${alpha("#8b5cf6", 0.2)}`,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PaidIcon sx={{ color: "#8b5cf6" }} />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        >
                          Split Payment Transactions
                        </Typography>
                        <Chip
                          label={`${dailyReportData.income.splitTransactions.length} transactions`}
                          size="small"
                          sx={{
                            backgroundColor: alpha("#8b5cf6", 0.2),
                            color: "#8b5cf6",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Bill No.
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Customer
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Description
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Cash
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Bank
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Total
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dailyReportData.income.splitTransactions.map(
                            (transaction, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: "monospace" }}
                                  >
                                    {transaction.billNumber}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {transaction.customerSupplier}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                    }}
                                  >
                                    {transaction.description}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#10b981" }}
                                  >
                                    ₹
                                    {transaction.cashAmount.toLocaleString(
                                      "en-IN"
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#2563eb" }}
                                  >
                                    ₹
                                    {transaction.bankAmount.toLocaleString(
                                      "en-IN"
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: "#8b5cf6" }}
                                  >
                                    ₹
                                    {transaction.amount.toLocaleString("en-IN")}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {/* Income Summary Cards */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: "16px",
                        background: isDark
                          ? "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)"
                          : "linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.06) 100%)",
                        border: `1px solid ${alpha("#10b981", 0.3)}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 1 }}
                      >
                        Total Cash Income
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: "#10b981" }}
                      >
                        ₹
                        {dailyReportData.summary.totalIncomeCash.toLocaleString(
                          "en-IN"
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: "16px",
                        background: isDark
                          ? "linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(29, 78, 216, 0.1) 100%)"
                          : "linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.06) 100%)",
                        border: `1px solid ${alpha("#2563eb", 0.3)}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 1 }}
                      >
                        Total Bank Income
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: "#2563eb" }}
                      >
                        ₹
                        {dailyReportData.summary.totalIncomeBank.toLocaleString(
                          "en-IN"
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: "16px",
                        background: isDark
                          ? "linear-gradient(135deg, rgba(16, 185, 129, 0.25) 0%, rgba(5, 150, 105, 0.2) 100%)"
                          : "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.12) 100%)",
                        border: `2px solid ${alpha("#10b981", 0.4)}`,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 1 }}
                      >
                        Grand Total Income
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: 900, color: "#10b981" }}
                      >
                        ₹
                        {dailyReportData.summary.totalIncome.toLocaleString(
                          "en-IN"
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Right Side - Expense Section */}
              <Box
                sx={{
                  flex: 1,
                  p: 4,
                  overflow: "auto",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: isDark
                        ? "linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.15) 100%)"
                        : "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.08) 100%)",
                      border: `2px solid ${alpha("#ef4444", 0.3)}`,
                      mr: 3,
                    }}
                  >
                    <TrendingDownIcon sx={{ fontSize: 28, color: "#ef4444" }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: isDark ? "#f1f5f9" : "#1e293b",
                      }}
                    >
                      Expense Summary
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                    >
                      Detailed breakdown of all expense transactions
                    </Typography>
                  </Box>
                </Box>

                {/* Cash Expense Transactions */}
                {dailyReportData.expense.cashTransactions.length > 0 && (
                  <Paper
                    sx={{ mb: 3, borderRadius: "16px", overflow: "hidden" }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        background: isDark
                          ? "linear-gradient(90deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%)"
                          : "linear-gradient(90deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.06) 100%)",
                        borderBottom: `1px solid ${alpha("#ef4444", 0.2)}`,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LocalAtmIcon sx={{ color: "#ef4444" }} />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        >
                          Cash Expense Transactions
                        </Typography>
                        <Chip
                          label={`${dailyReportData.expense.cashTransactions.length} transactions`}
                          size="small"
                          sx={{
                            backgroundColor: alpha("#ef4444", 0.2),
                            color: "#ef4444",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Bill No.
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Supplier
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Description
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Amount
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dailyReportData.expense.cashTransactions.map(
                            (transaction, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: "monospace" }}
                                  >
                                    {transaction.billNumber}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {transaction.customerSupplier}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                    }}
                                  >
                                    {transaction.description}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#ef4444" }}
                                  >
                                    ₹
                                    {transaction.amount.toLocaleString("en-IN")}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {/* Bank Expense Transactions */}
                {dailyReportData.expense.bankTransactions.length > 0 && (
                  <Paper
                    sx={{ mb: 3, borderRadius: "16px", overflow: "hidden" }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        background: isDark
                          ? "linear-gradient(90deg, rgba(37, 99, 235, 0.15) 0%, rgba(29, 78, 216, 0.1) 100%)"
                          : "linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.06) 100%)",
                        borderBottom: `1px solid ${alpha("#2563eb", 0.2)}`,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <AccountBalanceIcon sx={{ color: "#2563eb" }} />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        >
                          Bank Expense Transactions
                        </Typography>
                        <Chip
                          label={`${dailyReportData.expense.bankTransactions.length} transactions`}
                          size="small"
                          sx={{
                            backgroundColor: alpha("#2563eb", 0.2),
                            color: "#2563eb",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Bill No.
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Supplier
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Description
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Amount
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dailyReportData.expense.bankTransactions.map(
                            (transaction, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: "monospace" }}
                                  >
                                    {transaction.billNumber}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {transaction.customerSupplier}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                    }}
                                  >
                                    {transaction.description}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#2563eb" }}
                                  >
                                    ₹
                                    {transaction.amount.toLocaleString("en-IN")}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {/* Split Expense Transactions */}
                {dailyReportData.expense.splitTransactions.length > 0 && (
                  <Paper
                    sx={{ mb: 3, borderRadius: "16px", overflow: "hidden" }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        background: isDark
                          ? "linear-gradient(90deg, rgba(168, 85, 247, 0.15) 0%, rgba(147, 51, 234, 0.1) 100%)"
                          : "linear-gradient(90deg, rgba(168, 85, 247, 0.08) 0%, rgba(147, 51, 234, 0.06) 100%)",
                        borderBottom: `1px solid ${alpha("#8b5cf6", 0.2)}`,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PaidIcon sx={{ color: "#8b5cf6" }} />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        >
                          Split Payment Transactions
                        </Typography>
                        <Chip
                          label={`${dailyReportData.expense.splitTransactions.length} transactions`}
                          size="small"
                          sx={{
                            backgroundColor: alpha("#8b5cf6", 0.2),
                            color: "#8b5cf6",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Bill No.
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Supplier
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Description
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Cash
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Bank
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              Total
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dailyReportData.expense.splitTransactions.map(
                            (transaction, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: "monospace" }}
                                  >
                                    {transaction.billNumber}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {transaction.customerSupplier}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                    }}
                                  >
                                    {transaction.description}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#ef4444" }}
                                  >
                                    ₹
                                    {transaction.cashAmount.toLocaleString(
                                      "en-IN"
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#2563eb" }}
                                  >
                                    ₹
                                    {transaction.bankAmount.toLocaleString(
                                      "en-IN"
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: "#8b5cf6" }}
                                  >
                                    ₹
                                    {transaction.amount.toLocaleString("en-IN")}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {/* Expense Summary Cards */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: "16px",
                        background: isDark
                          ? "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%)"
                          : "linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.06) 100%)",
                        border: `1px solid ${alpha("#ef4444", 0.3)}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 1 }}
                      >
                        Total Cash Expense
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: "#ef4444" }}
                      >
                        ₹
                        {dailyReportData.summary.totalExpenseCash.toLocaleString(
                          "en-IN"
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: "16px",
                        background: isDark
                          ? "linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(29, 78, 216, 0.1) 100%)"
                          : "linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.06) 100%)",
                        border: `1px solid ${alpha("#2563eb", 0.3)}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 1 }}
                      >
                        Total Bank Expense
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: "#2563eb" }}
                      >
                        ₹
                        {dailyReportData.summary.totalExpenseBank.toLocaleString(
                          "en-IN"
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: "16px",
                        background: isDark
                          ? "linear-gradient(135deg, rgba(239, 68, 68, 0.25) 0%, rgba(220, 38, 38, 0.2) 100%)"
                          : "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.12) 100%)",
                        border: `2px solid ${alpha("#ef4444", 0.4)}`,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 1 }}
                      >
                        Grand Total Expense
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: 900, color: "#ef4444" }}
                      >
                        ₹
                        {dailyReportData.summary.totalExpense.toLocaleString(
                          "en-IN"
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Bottom Section - Net Profit and Closing Balance */}
            <Box
              sx={{
                p: 3,
                borderTop: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
                background: isDark
                  ? "linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.9) 100%)"
                  : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
                backdropFilter: "blur(20px)",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: isDark ? "#f1f5f9" : "#1e293b",
                  mb: 3,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  "&::before, &::after": {
                    content: '""',
                    flex: 1,
                    height: "1px",
                    background: isDark
                      ? "linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.3), transparent)"
                      : "linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.2), transparent)",
                  },
                }}
              >
                <DiamondIcon
                  sx={{
                    fontSize: 24,
                    color: isDark ? "#60a5fa" : "#3b82f6",
                    opacity: 0.8,
                  }}
                />
                Daily Performance Summary
                <DiamondIcon
                  sx={{
                    fontSize: 24,
                    color: isDark ? "#60a5fa" : "#3b82f6",
                    opacity: 0.8,
                  }}
                />
              </Typography>

              <Grid container spacing={3}>
                {/* Net Profit/Loss Card - Enhanced */}
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "20px",
                      background:
                        dailyReportData.summary.netProfit >= 0
                          ? isDark
                            ? "linear-gradient(145deg, rgba(16, 185, 129, 0.25) 0%, rgba(5, 150, 105, 0.2) 50%, rgba(34, 197, 94, 0.15) 100%)"
                            : "linear-gradient(145deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.12) 50%, rgba(34, 197, 94, 0.1) 100%)"
                          : isDark
                            ? "linear-gradient(145deg, rgba(239, 68, 68, 0.25) 0%, rgba(220, 38, 38, 0.2) 50%, rgba(248, 113, 113, 0.15) 100%)"
                            : "linear-gradient(145deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.12) 50%, rgba(248, 113, 113, 0.1) 100%)",
                      border: `1px solid ${alpha(
                        dailyReportData.summary.netProfit >= 0
                          ? "#10b981"
                          : "#ef4444",
                        0.3
                      )}`,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background:
                          dailyReportData.summary.netProfit >= 0
                            ? "linear-gradient(90deg, #10b981, #34d399, #10b981)"
                            : "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
                        animation: "shine 3s infinite linear",
                      },
                      "@keyframes shine": {
                        "0%": { backgroundPosition: "-200px 0" },
                        "100%": { backgroundPosition: "calc(200px + 100%) 0" },
                      },
                      boxShadow:
                        dailyReportData.summary.netProfit >= 0
                          ? "0 10px 40px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                          : "0 10px 40px rgba(239, 68, 68, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {/* Status Badge */}
                      <Chip
                        label={
                          dailyReportData.summary.netProfit >= 0
                            ? "PROFIT"
                            : "LOSS"
                        }
                        size="medium"
                        sx={{
                          mb: 2,
                          backgroundColor:
                            dailyReportData.summary.netProfit >= 0
                              ? alpha("#10b981", 0.2)
                              : alpha("#ef4444", 0.2),
                          color:
                            dailyReportData.summary.netProfit >= 0
                              ? "#10b981"
                              : "#ef4444",
                          fontWeight: 800,
                          letterSpacing: "1px",
                          fontSize: "0.75rem",
                          height: 28,
                          border: `1px solid ${alpha(
                            dailyReportData.summary.netProfit >= 0
                              ? "#10b981"
                              : "#ef4444",
                            0.3
                          )}`,
                          boxShadow:
                            dailyReportData.summary.netProfit >= 0
                              ? "0 4px 12px rgba(16, 185, 129, 0.2)"
                              : "0 4px 12px rgba(239, 68, 68, 0.2)",
                        }}
                      />

                      {/* Amount with Icon */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 3,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 70,
                            height: 70,
                            borderRadius: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                              dailyReportData.summary.netProfit >= 0
                                ? "linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(5, 150, 105, 0.2) 100%)"
                                : "linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(220, 38, 38, 0.2) 100%)",
                            border: `2px solid ${alpha(
                              dailyReportData.summary.netProfit >= 0
                                ? "#10b981"
                                : "#ef4444",
                              0.4
                            )}`,
                            boxShadow:
                              dailyReportData.summary.netProfit >= 0
                                ? "0 8px 24px rgba(16, 185, 129, 0.3)"
                                : "0 8px 24px rgba(239, 68, 68, 0.3)",
                          }}
                        >
                          {dailyReportData.summary.netProfit >= 0 ? (
                            <TrendingUpIcon
                              sx={{
                                fontSize: 36,
                                color: "#10b981",
                                filter:
                                  "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                              }}
                            />
                          ) : (
                            <TrendingDownIcon
                              sx={{
                                fontSize: 36,
                                color: "#ef4444",
                                filter:
                                  "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                              }}
                            />
                          )}
                        </Box>

                        <Box>
                          <Typography
                            variant="h2"
                            sx={{
                              fontWeight: 900,
                              color:
                                dailyReportData.summary.netProfit >= 0
                                  ? "#10b981"
                                  : "#ef4444",
                              fontSize: "2.75rem",
                              lineHeight: 1,
                              textShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            }}
                          >
                            ₹
                            {Math.abs(
                              dailyReportData.summary.netProfit
                            ).toLocaleString("en-IN")}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? "#94a3b8" : "#64748b",
                              display: "block",
                              textAlign: "center",
                              mt: 0.5,
                            }}
                          >
                            {dailyReportData.summary.netProfit >= 0
                              ? "Net Gain"
                              : "Net Loss"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Percentage Indicator */}
                      {dailyReportData.summary.totalIncome > 0 && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: "12px",
                            background: alpha(
                              dailyReportData.summary.netProfit >= 0
                                ? "#10b981"
                                : "#ef4444",
                              0.1
                            ),
                            border: `1px solid ${alpha(
                              dailyReportData.summary.netProfit >= 0
                                ? "#10b981"
                                : "#ef4444",
                              0.2
                            )}`,
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? "#cbd5e1" : "#475569",
                              fontWeight: 600,
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            Margin
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 800,
                              color:
                                dailyReportData.summary.netProfit >= 0
                                  ? "#10b981"
                                  : "#ef4444",
                            }}
                          >
                            {(
                              (dailyReportData.summary.netProfit /
                                dailyReportData.summary.totalIncome) *
                              100 || 0
                            ).toFixed(1)}
                            %
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* Closing Balance Cards - Enhanced */}
                <Grid item xs={12} md={8}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      height: "100%",
                    }}
                  >
                    {/* Header with subtle icon */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                        mb: 1,
                      }}
                    >
                      <AccountBalanceWallet
                        sx={{
                          fontSize: 28,
                          color: isDark ? "#60a5fa" : "#3b82f6",
                          opacity: 0.8,
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          color: isDark ? "#f1f5f9" : "#1e293b",
                          fontWeight: 700,
                          background: isDark
                            ? "linear-gradient(135deg, #60a5fa, #3b82f6)"
                            : "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                        }}
                      >
                        Closing Balances
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      {/* Cash Balance Card */}
                      <Grid item xs={12} sm={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: "16px",
                            background: isDark
                              ? "linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.15) 100%)"
                              : "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.08) 100%)",
                            border: `1px solid ${alpha("#10b981", 0.3)}`,
                            position: "relative",
                            overflow: "hidden",
                            transition:
                              "transform 0.2s ease, box-shadow 0.2s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 12px 32px rgba(16, 185, 129, 0.25)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "rgba(255, 255, 255, 0.2)",
                                mr: 2,
                                border: `1.5px solid ${alpha("#10b981", 0.4)}`,
                                boxShadow: "0 4px 12px rgba(16, 185, 129, 0.2)",
                              }}
                            >
                              <LocalAtmIcon
                                sx={{ fontSize: 22, color: "#10b981" }}
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  mb: 0.5,
                                  fontWeight: 600,
                                }}
                              >
                                Cash Balance
                              </Typography>
                              <Typography
                                variant="h4"
                                sx={{
                                  fontWeight: 800,
                                  color: "#10b981",
                                  fontSize: "1.75rem",
                                }}
                              >
                                ₹
                                {dailyReportData.summary.closingBalanceCash.toLocaleString(
                                  "en-IN"
                                )}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Change Indicator */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mt: 2,
                              p: 1.5,
                              borderRadius: "10px",
                              background: alpha("#10b981", 0.1),
                              border: `1px solid ${alpha("#10b981", 0.2)}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? "#cbd5e1" : "#475569",
                                fontWeight: 600,
                              }}
                            >
                              Daily Change
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 24,
                                  height: 24,
                                  borderRadius: "6px",
                                  background:
                                    dailyReportData.summary
                                      .closingBalanceCash >=
                                      dailyReportData.openingBalanceCash
                                      ? alpha("#10b981", 0.2)
                                      : alpha("#ef4444", 0.2),
                                }}
                              >
                                {dailyReportData.summary.closingBalanceCash >=
                                  dailyReportData.openingBalanceCash ? (
                                  <ArrowUpward
                                    sx={{ fontSize: 16, color: "#10b981" }}
                                  />
                                ) : (
                                  <ArrowDownward
                                    sx={{ fontSize: 16, color: "#ef4444" }}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 800,
                                  color:
                                    dailyReportData.summary
                                      .closingBalanceCash >=
                                      dailyReportData.openingBalanceCash
                                      ? "#10b981"
                                      : "#ef4444",
                                }}
                              >
                                ₹
                                {Math.abs(
                                  dailyReportData.summary.closingBalanceCash -
                                  dailyReportData.openingBalanceCash
                                ).toLocaleString("en-IN")}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>

                      {/* Bank Balance Card */}
                      <Grid item xs={12} sm={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: "16px",
                            background: isDark
                              ? "linear-gradient(135deg, rgba(37, 99, 235, 0.2) 0%, rgba(29, 78, 216, 0.15) 100%)"
                              : "linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(29, 78, 216, 0.08) 100%)",
                            border: `1px solid ${alpha("#2563eb", 0.3)}`,
                            position: "relative",
                            overflow: "hidden",
                            transition:
                              "transform 0.2s ease, box-shadow 0.2s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 12px 32px rgba(37, 99, 235, 0.25)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "rgba(255, 255, 255, 0.2)",
                                mr: 2,
                                border: `1.5px solid ${alpha("#2563eb", 0.4)}`,
                                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.2)",
                              }}
                            >
                              <AccountBalanceIcon
                                sx={{ fontSize: 22, color: "#2563eb" }}
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  mb: 0.5,
                                  fontWeight: 600,
                                }}
                              >
                                Bank Balance
                              </Typography>
                              <Typography
                                variant="h4"
                                sx={{
                                  fontWeight: 800,
                                  color: "#2563eb",
                                  fontSize: "1.75rem",
                                }}
                              >
                                ₹
                                {dailyReportData.summary.closingBalanceBank.toLocaleString(
                                  "en-IN"
                                )}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Change Indicator */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mt: 2,
                              p: 1.5,
                              borderRadius: "10px",
                              background: alpha("#2563eb", 0.1),
                              border: `1px solid ${alpha("#2563eb", 0.2)}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? "#cbd5e1" : "#475569",
                                fontWeight: 600,
                              }}
                            >
                              Daily Change
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 24,
                                  height: 24,
                                  borderRadius: "6px",
                                  background:
                                    dailyReportData.summary
                                      .closingBalanceBank >=
                                      dailyReportData.openingBalanceBank
                                      ? alpha("#2563eb", 0.2)
                                      : alpha("#ef4444", 0.2),
                                }}
                              >
                                {dailyReportData.summary.closingBalanceBank >=
                                  dailyReportData.openingBalanceBank ? (
                                  <ArrowUpward
                                    sx={{ fontSize: 16, color: "#2563eb" }}
                                  />
                                ) : (
                                  <ArrowDownward
                                    sx={{ fontSize: 16, color: "#ef4444" }}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 800,
                                  color:
                                    dailyReportData.summary
                                      .closingBalanceBank >=
                                      dailyReportData.openingBalanceBank
                                      ? "#2563eb"
                                      : "#ef4444",
                                }}
                              >
                                ₹
                                {Math.abs(
                                  dailyReportData.summary.closingBalanceBank -
                                  dailyReportData.openingBalanceBank
                                ).toLocaleString("en-IN")}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                      {/* Total Closing Balance - Centerpiece */}
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: "16px",
                          background: isDark
                            ? "linear-gradient(135deg, rgba(168, 85, 247, 0.25) 0%, rgba(147, 51, 234, 0.2) 50%, rgba(139, 92, 246, 0.15) 100%)"
                            : "linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(147, 51, 234, 0.12) 50%, rgba(139, 92, 246, 0.1) 100%)",
                          border: `1px solid ${alpha("#8b5cf6", 0.3)}`,
                          position: "relative",
                          overflow: "hidden",
                          transition:
                            "transform 0.2s ease, box-shadow 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 12px 32px rgba(139, 92, 246, 0.25)",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {/* Icon Box - Similar to bank card */}
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: "12px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "rgba(255, 255, 255, 0.2)",
                              mr: 2,
                              border: `1.5px solid ${alpha("#8b5cf6", 0.4)}`,
                              boxShadow: "0 4px 12px rgba(139, 92, 246, 0.2)",
                            }}
                          >
                            <AccountBalanceWallet
                              sx={{ fontSize: 22, color: "#8b5cf6" }}
                            />
                          </Box>

                          {/* Content Area */}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: isDark ? "#94a3b8" : "#64748b",
                                mb: 0.5,
                                fontWeight: 600,
                              }}
                            >
                              Total Closing Balance
                            </Typography>
                            <Typography
                              variant="h4"
                              sx={{
                                fontWeight: 800,
                                color: "#8b5cf6",
                                fontSize: "2rem",
                              }}
                            >
                              ₹
                              {dailyReportData.summary.totalClosingBalance.toLocaleString(
                                "en-IN"
                              )}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: isDark ? "#a5b4fc" : "#818cf8",
                                display: "block",
                                mt: 0.5,
                                fontWeight: 500,
                              }}
                            >
                              Combined cash & bank holdings
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Dialog>
      {/* Transaction Details View Modal */}
      <Dialog
        open={viewTransactionDialog}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            background: isDark
              ? "linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)"
              : "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)",
            backdropFilter: "blur(30px)",
            border: `1px solid ${alpha(
              isDark ? "#475569" : "#e2e8f0",
              isDark ? 0.2 : 0.3
            )}`,
            boxShadow: "0 25px 80px rgba(0, 0, 0, 0.25)",
            maxHeight: "95vh",
            overflow: "hidden",
            width: '50%',
          },
        }}
      >
        {viewingTransaction && (
          <>
            <Box
              sx={{
                p: 3,
                borderBottom: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
                background: viewingTransaction.type === "income"
                  ? isDark
                    ? "linear-gradient(90deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)"
                    : "linear-gradient(90deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.08) 100%)"
                  : isDark
                    ? "linear-gradient(90deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%)"
                    : "linear-gradient(90deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.08) 100%)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: isDark ? "#f1f5f9" : "#1e293b",
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 35,
                        height: 35,
                        borderRadius: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: viewingTransaction.type === "income"
                          ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                          : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                        boxShadow: viewingTransaction.type === "income"
                          ? "0 8px 32px rgba(16, 185, 129, 0.3)"
                          : "0 8px 32px rgba(239, 68, 68, 0.3)",
                      }}
                    >
                      {viewingTransaction.type === "income" ? (
                        <TrendingUpIcon sx={{ fontSize: 25, color: "white" }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: 25, color: "white" }} />
                      )}
                    </Box>
                    Transaction Details
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: isDark ? "#94a3b8" : "#64748b",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <ReceiptIcon sx={{ fontSize: 20 }} />
                    {viewingTransaction.billNumber} • {format(parseISO(viewingTransaction.date), "dd/MM/yyyy")}
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleCloseViewDialog}
                  sx={{
                    backgroundColor: isDark
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.05)",
                    color: isDark ? "#94a3b8" : "#64748b",
                    "&:hover": {
                      backgroundColor: isDark
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Transaction Type Badge */}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
                <Chip
                  label={viewingTransaction.type === "income" ? "INCOME" : "EXPENSE"}
                  sx={{
                    backgroundColor: viewingTransaction.type === "income"
                      ? alpha("#10b981", 0.2)
                      : alpha("#ef4444", 0.2),
                    color: viewingTransaction.type === "income" ? "#10b981" : "#ef4444",
                    fontWeight: 800,
                    border: `1px solid ${alpha(
                      viewingTransaction.type === "income" ? "#10b981" : "#ef4444",
                      0.3
                    )}`,
                  }}
                />
                <Chip
                  label={viewingTransaction.transactionType === "customer"
                    ? "CUSTOMER TRANSACTION"
                    : viewingTransaction.transactionType === "supplier"
                      ? "SUPPLIER TRANSACTION"
                      : viewingTransaction.transactionType === "manual"
                        ? "MANUAL ENTRY"
                        : viewingTransaction.transactionType === "suspense"
                          ? "SUSPENSE ENTRY"
                          : viewingTransaction.transactionType === "adjustment"
                            ? "ADJUSTMENT ENTRY"
                            : "AUTO-GENERATED"
                  }
                  sx={{
                    backgroundColor: viewingTransaction.transactionType === "customer"
                      ? alpha("#3b82f6", 0.2)
                      : viewingTransaction.transactionType === "supplier"
                        ? alpha("#f59e0b", 0.2)
                        : viewingTransaction.transactionType === "manual"
                          ? alpha("#8b5cf6", 0.2)
                          : viewingTransaction.transactionType === "suspense"
                            ? alpha("#ec4899", 0.2)
                            : viewingTransaction.transactionType === "adjustment"
                              ? alpha("#f59e0b", 0.2)
                              : alpha("#64748b", 0.2),
                    color: viewingTransaction.transactionType === "customer"
                      ? "#3b82f6"
                      : viewingTransaction.transactionType === "supplier"
                        ? "#f59e0b"
                        : viewingTransaction.transactionType === "manual"
                          ? "#8b5cf6"
                          : viewingTransaction.transactionType === "suspense"
                            ? "#ec4899"
                            : viewingTransaction.transactionType === "adjustment"
                              ? "#f59e0b"
                              : "#64748b",
                    fontWeight: 600,
                    border: `1px solid ${alpha(
                      viewingTransaction.transactionType === "customer"
                        ? "#3b82f6"
                        : viewingTransaction.transactionType === "supplier"
                          ? "#f59e0b"
                          : viewingTransaction.transactionType === "manual"
                            ? "#8b5cf6"
                            : viewingTransaction.transactionType === "suspense"
                              ? "#ec4899"
                              : viewingTransaction.transactionType === "adjustment"
                                ? "#f59e0b"
                                : "#64748b",
                      0.3
                    )}`,
                  }}
                />
                {viewingTransaction.isAutoGenerated && (
                  <Chip
                    label="AUTO-GENERATED"
                    size="small"
                    sx={{
                      backgroundColor: alpha("#3b82f6", 0.1),
                      color: "#3b82f6",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ p: 2, overflow: "auto", maxHeight: "80vh" }}>
              <Grid container spacing={3}>
                {/* Left Column - Basic Information */}
                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      background: isDark
                        ? "rgba(30, 41, 59, 0.5)"
                        : "rgba(255, 255, 255, 0.7)",
                      border: `1px solid ${alpha(
                        isDark ? "#475569" : "#e2e8f0",
                        isDark ? 0.2 : 0.3
                      )}`,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        color: isDark ? "#f1f5f9" : "#1e293b",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <InfoIcon sx={{ fontSize: 24, color: "#3b82f6" }} />
                      Basic Information
                    </Typography>

                    <Stack spacing={2}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Bill Number
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            color: isDark ? "#f1f5f9" : "#1e293b",
                            fontSize: "1.1rem",
                          }}
                        >
                          {viewingTransaction.billNumber}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Date
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: isDark ? "#f1f5f9" : "#1e293b",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <CalendarToday sx={{ fontSize: 16 }} />
                          {format(parseISO(viewingTransaction.date), "dd/MM/yyyy")}
                          <Typography
                            variant="caption"
                            sx={{
                              ml: 2,
                              color: isDark ? "#60a5fa" : "#2563eb",
                              fontWeight: 600,
                            }}
                          >
                            {format(parseISO(viewingTransaction.date), "EEEE")}
                          </Typography>
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          {viewingTransaction.type === "income" ? "Customer" : "Supplier"}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: viewingTransaction.type === "income"
                                ? alpha("#10b981", 0.2)
                                : alpha("#ef4444", 0.2),
                              color: viewingTransaction.type === "income" ? "#10b981" : "#ef4444",
                              border: `2px solid ${alpha(
                                viewingTransaction.type === "income" ? "#10b981" : "#ef4444",
                                0.3
                              )}`,
                            }}
                          >
                            {viewingTransaction.customerSupplier?.charAt(0) || "?"}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#f1f5f9" : "#1e293b",
                              }}
                            >
                              {viewingTransaction.customerSupplier}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? "#94a3b8" : "#64748b",
                              }}
                            >
                              {viewingTransaction.transactionType === "customer"
                                ? "Customer Account"
                                : viewingTransaction.transactionType === "supplier"
                                  ? "Supplier Account"
                                  : "Manual Entry"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Description
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: isDark ? "#f1f5f9" : "#1e293b",
                            p: 2,
                            borderRadius: "8px",
                            background: isDark
                              ? "rgba(255, 255, 255, 0.05)"
                              : "rgba(0, 0, 0, 0.02)",
                            border: `1px solid ${alpha(
                              isDark ? "#475569" : "#e2e8f0",
                              isDark ? 0.1 : 0.2
                            )}`,
                            minHeight: "60px",
                          }}
                        >
                          {viewingTransaction.description}
                        </Typography>
                      </Box>

                      {viewingTransaction.category && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? "#94a3b8" : "#64748b",
                              fontWeight: 600,
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            Category
                          </Typography>
                          <Chip
                            label={viewingTransaction.category}
                            sx={{
                              backgroundColor: isDark
                                ? "rgba(255, 255, 255, 0.1)"
                                : "rgba(0, 0, 0, 0.05)",
                              color: isDark ? "#f1f5f9" : "#1e293b",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                </Grid>

                {/* Right Column - Financial Details */}
                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      background: isDark
                        ? "rgba(30, 41, 59, 0.5)"
                        : "rgba(255, 255, 255, 0.7)",
                      border: `1px solid ${alpha(
                        isDark ? "#475569" : "#e2e8f0",
                        isDark ? 0.2 : 0.3
                      )}`,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        color: isDark ? "#f1f5f9" : "#1e293b",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <AccountBalanceWallet sx={{ fontSize: 24, color: "#3b82f6" }} />
                      Financial Details
                    </Typography>

                    <Stack spacing={3}>
                      {/* Total Amount Card */}
                      <Paper
                        sx={{
                          p: 2.5,
                          borderRadius: "12px",
                          background: viewingTransaction.type === "income"
                            ? "linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.15) 100%)"
                            : "linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.15) 100%)",
                          border: `1px solid ${alpha(
                            viewingTransaction.type === "income" ? "#10b981" : "#ef4444",
                            0.3
                          )}`,
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontWeight: 600,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Total Amount
                        </Typography>
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 900,
                            color: viewingTransaction.type === "income" ? "#10b981" : "#ef4444",
                          }}
                        >
                          ₹{viewingTransaction.amount.toLocaleString("en-IN")}
                        </Typography>
                      </Paper>

                      {/* Payment Split */}
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontWeight: 600,
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <PaidIcon sx={{ fontSize: 20 }} />
                          Payment Split
                        </Typography>

                        <Grid container spacing={2}>
                          {/* Cash Amount */}
                          <Grid item xs={6}>
                            <Paper
                              sx={{
                                p: 2,
                                borderRadius: "10px",
                                background: viewingTransaction.cash > 0
                                  ? "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)"
                                  : isDark
                                    ? "rgba(255, 255, 255, 0.05)"
                                    : "rgba(0, 0, 0, 0.02)",
                                border: `1px solid ${viewingTransaction.cash > 0
                                    ? alpha("#10b981", 0.2)
                                    : isDark ? "#475569" : "#e2e8f0"
                                  }`,
                                textAlign: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 1,
                                  mb: 1,
                                }}
                              >
                                <LocalAtmIcon
                                  sx={{
                                    fontSize: 20,
                                    color: viewingTransaction.cash > 0 ? "#10b981" : isDark ? "#64748b" : "#94a3b8",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isDark ? "#94a3b8" : "#64748b",
                                    fontWeight: 600,
                                  }}
                                >
                                  Cash
                                </Typography>
                              </Box>
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 800,
                                  color: viewingTransaction.cash > 0 ? "#10b981" : isDark ? "#64748b" : "#94a3b8",
                                }}
                              >
                                ₹{viewingTransaction.cash.toLocaleString("en-IN")}
                              </Typography>
                            </Paper>
                          </Grid>

                          {/* Bank Amount */}
                          <Grid item xs={6}>
                            <Paper
                              sx={{
                                p: 2,
                                borderRadius: "10px",
                                background: viewingTransaction.bank > 0
                                  ? "linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(29, 78, 216, 0.1) 100%)"
                                  : isDark
                                    ? "rgba(255, 255, 255, 0.05)"
                                    : "rgba(0, 0, 0, 0.02)",
                                border: `1px solid ${viewingTransaction.bank > 0
                                    ? alpha("#2563eb", 0.2)
                                    : isDark ? "#475569" : "#e2e8f0"
                                  }`,
                                textAlign: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 1,
                                  mb: 1,
                                }}
                              >
                                <AccountBalanceIcon
                                  sx={{
                                    fontSize: 20,
                                    color: viewingTransaction.bank > 0 ? "#2563eb" : isDark ? "#64748b" : "#94a3b8",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isDark ? "#94a3b8" : "#64748b",
                                    fontWeight: 600,
                                  }}
                                >
                                  Bank
                                </Typography>
                              </Box>
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 800,
                                  color: viewingTransaction.bank > 0 ? "#2563eb" : isDark ? "#64748b" : "#94a3b8",
                                }}
                              >
                                ₹{viewingTransaction.bank.toLocaleString("en-IN")}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        {/* Payment Mode */}
                        <Box sx={{ mt: 2, textAlign: "center" }}>
                          <Chip
                            label={
                              viewingTransaction.paymentMode === "cash"
                                ? "Cash Only"
                                : viewingTransaction.paymentMode === "bank"
                                  ? "Bank Only"
                                  : "Cash & Bank Split"
                            }
                            sx={{
                              backgroundColor:
                                viewingTransaction.paymentMode === "cash"
                                  ? alpha("#10b981", 0.2)
                                  : viewingTransaction.paymentMode === "bank"
                                    ? alpha("#2563eb", 0.2)
                                    : alpha("#8b5cf6", 0.2),
                              color:
                                viewingTransaction.paymentMode === "cash"
                                  ? "#10b981"
                                  : viewingTransaction.paymentMode === "bank"
                                    ? "#2563eb"
                                    : "#8b5cf6",
                              fontWeight: 700,
                              border: `1px solid ${alpha(
                                viewingTransaction.paymentMode === "cash"
                                  ? "#10b981"
                                  : viewingTransaction.paymentMode === "bank"
                                    ? "#2563eb"
                                    : "#8b5cf6",
                                0.3
                              )}`,
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Status Information */}
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontWeight: 600,
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <ReceiptIcon sx={{ fontSize: 20 }} />
                          Transaction Status
                        </Typography>

                        <Grid container spacing={2}>
                          {/* For Income Transactions */}
                          {viewingTransaction.type === "income" && (
                            <>
                              <Grid item xs={6}>
                                <Paper
                                  sx={{
                                    p: 2,
                                    borderRadius: "10px",
                                    background: isDark
                                      ? "rgba(255, 255, 255, 0.05)"
                                      : "rgba(0, 0, 0, 0.02)",
                                    border: `1px solid ${alpha(
                                      isDark ? "#475569" : "#e2e8f0",
                                      isDark ? 0.1 : 0.2
                                    )}`,
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 600,
                                      display: "block",
                                      mb: 1,
                                    }}
                                  >
                                    Received
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 800,
                                      color: "#10b981",
                                    }}
                                  >
                                    ₹{viewingTransaction.totalReceived?.toLocaleString("en-IN") || "0"}
                                  </Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={6}>
                                <Paper
                                  sx={{
                                    p: 2,
                                    borderRadius: "10px",
                                    background: viewingTransaction.totalPending > 0
                                      ? "linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%)"
                                      : isDark
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : "rgba(0, 0, 0, 0.02)",
                                    border: `1px solid ${viewingTransaction.totalPending > 0
                                        ? alpha("#f59e0b", 0.2)
                                        : isDark ? "#475569" : "#e2e8f0"
                                      }`,
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 600,
                                      display: "block",
                                      mb: 1,
                                    }}
                                  >
                                    Pending
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 800,
                                      color: viewingTransaction.totalPending > 0 ? "#f59e0b" : "#10b981",
                                    }}
                                  >
                                    ₹{viewingTransaction.totalPending?.toLocaleString("en-IN") || "0"}
                                  </Typography>
                                </Paper>
                              </Grid>
                            </>
                          )}

                          {/* For Expense Transactions */}
                          {viewingTransaction.type === "expense" && (
                            <>
                              <Grid item xs={6}>
                                <Paper
                                  sx={{
                                    p: 2,
                                    borderRadius: "10px",
                                    background: isDark
                                      ? "rgba(255, 255, 255, 0.05)"
                                      : "rgba(0, 0, 0, 0.02)",
                                    border: `1px solid ${alpha(
                                      isDark ? "#475569" : "#e2e8f0",
                                      isDark ? 0.1 : 0.2
                                    )}`,
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 600,
                                      display: "block",
                                      mb: 1,
                                    }}
                                  >
                                    Paid
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 800,
                                      color: "#10b981",
                                    }}
                                  >
                                    ₹{viewingTransaction.totalPaid?.toLocaleString("en-IN") || "0"}
                                  </Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={6}>
                                <Paper
                                  sx={{
                                    p: 2,
                                    borderRadius: "10px",
                                    background: viewingTransaction.totalPending > 0
                                      ? "linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%)"
                                      : isDark
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : "rgba(0, 0, 0, 0.02)",
                                    border: `1px solid ${viewingTransaction.totalPending > 0
                                        ? alpha("#f59e0b", 0.2)
                                        : isDark ? "#475569" : "#e2e8f0"
                                      }`,
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 600,
                                      display: "block",
                                      mb: 1,
                                    }}
                                  >
                                    Pending
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 800,
                                      color: viewingTransaction.totalPending > 0 ? "#f59e0b" : "#10b981",
                                    }}
                                  >
                                    ₹{viewingTransaction.totalPending?.toLocaleString("en-IN") || "0"}
                                  </Typography>
                                </Paper>
                              </Grid>
                            </>
                          )}

                          {/* Fine Information for Expense */}
                          {viewingTransaction.type === "expense" && (
                            <>
                              <Grid item xs={6}>
                                <Paper
                                  sx={{
                                    p: 2,
                                    borderRadius: "10px",
                                    background: viewingTransaction.fine > 0
                                      ? "linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%)"
                                      : isDark
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : "rgba(0, 0, 0, 0.02)",
                                    border: `1px solid ${viewingTransaction.fine > 0
                                        ? alpha("#f59e0b", 0.2)
                                        : isDark ? "#475569" : "#e2e8f0"
                                      }`,
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 600,
                                      display: "block",
                                      mb: 1,
                                    }}
                                  >
                                    Fine Due
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 800,
                                      color: "#f59e0b",
                                    }}
                                  >
                                    {viewingTransaction.fine || 0} gm
                                  </Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={6}>
                                <Paper
                                  sx={{
                                    p: 2,
                                    borderRadius: "10px",
                                    background: viewingTransaction.finePaid > 0
                                      ? "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)"
                                      : isDark
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : "rgba(0, 0, 0, 0.02)",
                                    border: `1px solid ${viewingTransaction.finePaid > 0
                                        ? alpha("#10b981", 0.2)
                                        : isDark ? "#475569" : "#e2e8f0"
                                      }`,
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontWeight: 600,
                                      display: "block",
                                      mb: 1,
                                    }}
                                  >
                                    Fine Paid
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 800,
                                      color: "#10b981",
                                    }}
                                  >
                                    {viewingTransaction.finePaid || 0} gm
                                  </Typography>
                                </Paper>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Bottom Row - Additional Information */}
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      background: isDark
                        ? "rgba(30, 41, 59, 0.5)"
                        : "rgba(255, 255, 255, 0.7)",
                      border: `1px solid ${alpha(
                        isDark ? "#475569" : "#e2e8f0",
                        isDark ? 0.2 : 0.3,
                      )}`,

                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        color: isDark ? "#f1f5f9" : "#1e293b",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <EditIcon sx={{ fontSize: 24, color: "#3b82f6" }} />
                      Additional Information
                    </Typography>

                    <Grid container spacing={3}>
                      {/* Left Column - Metadata */}
                      <Grid item xs={12} md={6}>
                        <Stack spacing={2}>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? "#94a3b8" : "#64748b",
                                fontWeight: 600,
                                display: "block",
                                mb: 0.5,
                              }}
                            >
                              Created
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isDark ? "#cbd5e1" : "#475569",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CalendarToday sx={{ fontSize: 14 }} />
                              {viewingTransaction.created_at
                                ? format(parseISO(viewingTransaction.created_at), "dd/MM/yyyy hh:mm a")
                                : "N/A"}
                            </Typography>
                          </Box>

                          {viewingTransaction.updated_at && viewingTransaction.updated_at !== viewingTransaction.created_at && (
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  fontWeight: 600,
                                  display: "block",
                                  mb: 0.5,
                                }}
                              >
                                Last Updated
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: isDark ? "#cbd5e1" : "#475569",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <RefreshIcon sx={{ fontSize: 14 }} />
                                {format(parseISO(viewingTransaction.updated_at), "dd/MM/yyyy hh:mm a")}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Grid>

                      {/* Right Column - Remarks */}
                      <Grid item xs={12} md={6}>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? "#94a3b8" : "#64748b",
                              fontWeight: 600,
                              display: "block",
                              mb: 1.5,
                              // display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                            Remarks
                          </Typography>
                          <Paper
                            sx={{
                              p: 3,
                              borderRadius: "12px",
                              background: isDark
                                ? "rgba(255, 255, 255, 0.05)"
                                : "rgba(0, 0, 0, 0.02)",
                              border: `1px solid ${alpha(
                                isDark ? "#475569" : "#e2e8f0",
                                isDark ? 0.1 : 0.2
                              )}`,
                              minHeight: "120px",
                            }}
                          >
                            {viewingTransaction.remark ? (
                              <Typography
                                variant="body1"
                                sx={{
                                  color: isDark ? "#cbd5e1" : "#475569",
                                  whiteSpace: "pre-wrap",
                                  lineHeight: 1.6,
                                }}
                              >
                                {viewingTransaction.remark}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: isDark ? "#64748b" : "#94a3b8",
                                  fontStyle: "italic",
                                  textAlign: "center",
                                  py: 4,
                                }}
                              >
                                No remarks provided
                              </Typography>
                            )}
                          </Paper>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            <DialogActions
              sx={{
                p: 2,
                borderTop: `1px solid ${alpha(
                  isDark ? "#475569" : "#e2e8f0",
                  isDark ? 0.2 : 0.3
                )}`,
                background: isDark
                  ? "rgba(15, 23, 42, 0.5)"
                  : "rgba(248, 250, 252, 0.5)",
              }}
            >

              {!viewingTransaction.isAutoGenerated && !isPastDay() && !isDayClosed(selectedRoznama) && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    handleCloseViewDialog();
                    handleEditTransaction(viewingTransaction);
                  }}
                  sx={{
                    borderRadius: "12px",
                    px: 4,
                    fontWeight: 600,
                    background: isDark
                      ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
                      : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                  }}
                >
                  Edit Transaction
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default JewelleryDaybook;
